import React, { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useLocation, useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import Tooltip from "@mui/material/Tooltip";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FlagLoader from "layouts/flagloader";

import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftPagination from "components/SoftPagination";
import SoftSelect from "components/SoftSelect";
import SoftSnackbar from "components/SoftSnackbar";
import SoftTypography from "components/SoftTypography";

import { API_URL } from "config";
import { useAuth } from "../../../AuthContext";
import { useProjectPermissions } from "../projectPermissions";
import { ProjectCommunicationComposerDialog } from "../components/ProjectCommunicationDialog";
import ProjectLiveChatBadge from "layouts/project-live-chat/ProjectLiveChatBadge";
import { useProjectLiveChat } from "layouts/project-live-chat/useProjectLiveChat";
import VerisoulResultDialog from "../components/VerisoulResultDialog";
import StatsHoverTooltip from "../components/StatsHoverTooltip";
import CompleteDeviceMetric from "../CompleteDeviceMetric";
import ProjectTableSortLabel, {
  epcSortValue,
  sortTableRows,
} from "../components/ProjectTableSortLabel";
import {
  projectFilterSearchSuffix,
  projectFilterStateFromSearch,
  writeProjectFiltersToParams,
} from "../projectFilterQuery";

const projectTabs = [
  { value: "won", label: "Won" },
  { value: "live", label: "Live" },
  { value: "pause", label: "Pause" },
  { value: "auto_pause", label: "Auto Pause" },
  { value: "invoiced", label: "Invoiced" },
  { value: "invoice_at_zero", label: "Invoice at Zero" },
  { value: "closed", label: "Closed" },
];

const projectTabValues = projectTabs.map((tab) => tab.value);

const projectTabFromSearch = (search) => {
  const tab = new URLSearchParams(search || "").get("tab");
  return projectTabValues.includes(tab) ? tab : "live";
};

const recentlyActiveFromSearch = (search) =>
  new URLSearchParams(search || "").get("recent_active") === "1";

const tabStyles = {
  won: { color: "#16a34a", background: "#dcfce7", border: "#86efac" },
  live: { color: "#0ea5e9", background: "#e0f2fe", border: "#7dd3fc" },
  pause: { color: "#f6a800", background: "#fff7df", border: "#ffd666" },
  auto_pause: { color: "#dc2626", background: "#fef2f2", border: "#fca5a5" },
  invoiced: { color: "#2563eb", background: "#dbeafe", border: "#93c5fd" },
  invoice_at_zero: { color: "#475569", background: "#f1f5f9", border: "#cbd5e1" },
  closed: { color: "#ef4444", background: "#fee2e2", border: "#fecaca" },
};

const projectStatusActions = [
  { value: "soft_launch", label: "Soft Launch", color: "#f6a800" },
  { value: "full_launch", label: "Full Launch", color: "#52c41a" },
  { value: "live", label: "Live", color: "#ff4d6d" },
  { value: "pause", label: "Pause", color: "#64748b" },
  { value: "invoiced", label: "Invoiced", color: "#0ea5e9" },
  { value: "invoice_at_zero", label: "Invoice at Zero", color: "#344767" },
  { value: "closed", label: "Closed", color: "#475569" },
];

const liveStatuses = ["soft_launch", "full_launch", "hard_launch", "live"];

const exportFilenamePart = (value) =>
  String(value || "projects")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "projects";

const tableHeaderCellSx = {
  backgroundColor: "#f8f9fa",
  color: "#8392ab",
  fontSize: "12px",
  fontWeight: 400,
  letterSpacing: 0,
  textTransform: "capitalize",
  borderBottom: "1px solid #e9ecef",
  py: 0.75,
  px: 0.45,
  whiteSpace: "normal",
  lineHeight: 1.15,
  verticalAlign: "middle",
};

const tableBodyCellSx = {
  color: "#334155",
  fontSize: "10px",
  fontWeight: 400,
  borderBottom: "1px solid #edf2f7",
  py: 0.4,
  px: 0.45,
  verticalAlign: "middle",
  lineHeight: 1.15,
  overflowWrap: "anywhere",
};

const textOrDash = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  return value;
};

const formatIrDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (!Number.isFinite(number)) return value;
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const numericValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

const epcText = (revenue, breakdown, stats = {}) => {
  const overall = breakdown?.overall || {};
  const clicks = numericValue(overall.clicks ?? stats.total);
  const totalRevenue = numericValue(revenue);

  if (clicks === null || clicks <= 0 || totalRevenue === null) return "-";
  return `$${(totalRevenue / clicks).toFixed(2)}`;
};

const roundedMetricText = (value) => {
  const number = numericValue(value);
  if (number === null) return "-";
  const whole = Math.floor(number);
  return number - whole > 0.5 ? whole + 1 : whole;
};

const metricNumberText = (value, prefix = "") => {
  const number = numericValue(value);
  if (number === null) return "-";
  const formatted = Number.isInteger(number)
    ? String(number)
    : number.toFixed(2).replace(/\.?0+$/, "");

  return `${prefix}${formatted}`;
};

const loiMetricColor = (average, configured) => {
  const averageValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (averageValue === null || configuredValue === null) return "#334155";
  return Math.abs(averageValue - configuredValue) <= 5 ? "#15803d" : "#dc2626";
};

const irMetricColor = (average, configured) => {
  const actualValue = numericValue(average);
  const configuredValue = numericValue(configured);

  if (actualValue === null || configuredValue === null) return "#334155";
  return Math.abs(actualValue - configuredValue) <= 10 ? "#15803d" : "#dc2626";
};

function IrMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: irMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {formatIrDisplay(average)}
      </SoftBox>
      /{formatIrDisplay(configured)}
    </SoftBox>
  );
}

IrMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

function LoiMetric({ average, configured }) {
  return (
    <SoftBox component="span" sx={{ color: "#334155", fontWeight: 400 }}>
      <SoftBox
        component="span"
        sx={{
          color: loiMetricColor(average, configured),
          fontWeight: numericValue(average) === null ? 400 : 700,
        }}
      >
        {roundedMetricText(average)}
      </SoftBox>
      /{roundedMetricText(configured)}
    </SoftBox>
  );
}

LoiMetric.propTypes = {
  average: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  configured: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const digitsOnly = (value, maxDigits = 7) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, maxDigits);

const decimalOnly = (value, maxDigits = 7, maxDecimals = 2) => {
  const raw = String(value || "").replace(/[^\d.]/g, "");
  const [whole = "", decimal = ""] = raw.split(".");
  const normalizedWhole = whole.slice(0, maxDigits);

  return raw.includes(".") ? `${normalizedWhole}.${decimal.slice(0, maxDecimals)}` : normalizedWhole;
};

const quotaText = (value) => {
  const quota = Number(value || 0);
  return Number.isFinite(quota) && quota > 0 ? quota : "QS";
};

const toDatetimeLocalValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const projectTabFromStatus = (status) => {
  const normalized = String(status || "won").toLowerCase();
  if (liveStatuses.includes(normalized)) return "live";
  return ["pause", "invoiced", "invoice_at_zero", "closed"].includes(normalized)
    ? normalized
    : "won";
};

const clientText = (project) => {
  const name = project.client_name || project.client?.name || "";

  if (name) return name;
  return String(project.label || "").replace(/\s*\([^)]*\)\s*$/, "").trim();
};

const shortClientText = (project, maxLength = 10) => {
  const name = clientText(project);
  if (!name) return "-";

  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
};

const studyTypeText = (project) => project.project_type_master?.name || project.project_type || "";

const projectTypeText = (project) => {
  const option = typeof project.project_type_option === "object" ? project.project_type_option : null;
  return option?.name || option?.label || project.project_type_option || "";
};

const projectDetailValue = (project, key) => {
  if (project[key] !== null && project[key] !== undefined && project[key] !== "") {
    if (key === "ir") return formatIrDisplay(project[key]);
    return project[key];
  }

  if (!Array.isArray(project.details)) return "-";

  const detail = project.details.find(
    (item) => item[key] !== null && item[key] !== undefined && item[key] !== ""
  );

  if (!detail) return "-";
  return key === "ir" ? formatIrDisplay(detail[key]) : detail[key];
};

const projectDetailRawValue = (project, key) => {
  if (project?.[key] !== null && project?.[key] !== undefined && project?.[key] !== "") {
    return project[key];
  }

  if (!Array.isArray(project?.details)) return "";

  const detail = project.details.find(
    (item) => item[key] !== null && item[key] !== undefined && item[key] !== ""
  );

  return detail ? detail[key] : "";
};

const projectAverageDetailValue = (project, key, { blankAsZero = false } = {}) => {
  if (Array.isArray(project?.details) && project.details.length > 0) {
    if (blankAsZero) {
      const total = project.details.reduce(
        (sum, detail) => sum + (numericValue(detail?.[key]) ?? 0),
        0
      );

      return total / project.details.length;
    }

    const detailValues = project.details
      .map((detail) => numericValue(detail?.[key]))
      .filter((value) => value !== null);

    if (detailValues.length > 0) {
      return detailValues.reduce((total, value) => total + value, 0) / detailValues.length;
    }
  }

  return projectDetailRawValue(project, key);
};

const projectConfiguredLoi = (project) => {
  return projectAverageDetailValue(project, "loi");
};

const projectConfiguredIr = (project) => {
  return projectAverageDetailValue(project, "ir");
};

const projectRequiredNValue = (project) => {
  return projectAverageDetailValue(project, "required_n", { blankAsZero: true });
};

const projectRequiredNText = (project) => {
  return metricNumberText(projectRequiredNValue(project));
};

const projectConfiguredCpi = (project) => {
  return projectAverageDetailValue(project, "won_cpi", { blankAsZero: true });
};

const selectOptionValue = (option) => option?.value ?? option?.id ?? null;

const toSelectOption = (item) => {
  if (!item) return null;
  const value = selectOptionValue(item);

  if (value === null || value === undefined || value === "") return null;

  return {
    ...item,
    value,
    label: item.label || item.name || item.code || String(value),
  };
};

const clientToSelectOption = (item) => {
  const label = item?.name || item?.client_name || clientText(item || {});

  return toSelectOption({
    ...item,
    label,
    name: label,
  });
};

const mergeSelectOptions = (options, selected) => {
  const merged = [];

  [
    ...(Array.isArray(selected) ? selected : selected ? [selected] : []),
    ...(Array.isArray(options) ? options : []),
  ].forEach((option) => {
    const normalized = toSelectOption(option);
    if (!normalized) return;

    if (!merged.some((item) => String(item.value) === String(normalized.value))) {
      merged.push(normalized);
    }
  });

  return merged;
};

const clientOptionFromProject = (project) => {
  const value = project?.client_id ?? project?.client?.id;
  const label = clientText(project || {});

  return toSelectOption({
    value,
    id: value,
    label,
    name: label,
  });
};

const projectManagerOptionFromProject = (project) => {
  const role = project?.project_manager_role || {};
  const value = project?.project_manager_role_id ?? role.id ?? role.value;
  const label = project?.project_manager_text || role.label || role.name;

  return toSelectOption({ ...role, value, id: value, label, name: label });
};

const projectManagerUserOptionFromProject = (project) => {
  const manager = project?.project_manager || {};
  const value = project?.project_manager_id ?? manager.id ?? manager.value;
  const label = manager.label || manager.name || manager.email;

  return toSelectOption({ ...manager, value, id: value, label, name: manager.name || label });
};

const studyTypeOptionFromProject = (project) => {
  const type = project?.project_type_master || {};
  const value = project?.project_type_id ?? type.id ?? type.value;
  const label = studyTypeText(project || {}) || type.label || type.name;

  return toSelectOption({ ...type, value, id: value, label, name: label });
};

const bidProjectTypeOptionFromProject = (project) => {
  const option =
    project?.project_type_option && typeof project.project_type_option === "object"
      ? project.project_type_option
      : {};
  const value = project?.project_type_option_id ?? option.id ?? option.value;
  const label = projectTypeText(project || {}) || option.label || option.name;

  return toSelectOption({ ...option, value, id: value, label, name: label });
};

const wonCpiText = (project) => {
  return metricNumberText(projectConfiguredCpi(project), "$");
};

const calculatedCpiText = (project) => {
  const metrics = project?.project_metrics || {};
  const clientRevenue = numericValue(metrics.client_revenue);
  const completes = numericValue(metrics.stats?.complete);
  const calculatedCpi = clientRevenue !== null && completes !== null && completes > 0
    ? clientRevenue / completes
    : metrics.calculated_cpi ?? 0;

  return metricNumberText(calculatedCpi, "$");
};

const emptyBreakdown = {
  "1h": { complete: 0, clicks: 0, v_rtr: null },
  "12h": { complete: 0, clicks: 0, v_rtr: null },
  "24h": { complete: 0, clicks: 0, v_rtr: null },
  overall: { complete: 0, clicks: 0, v_rtr: null },
};

const metricPeriods = [
  { key: "1h", label: "1" },
  { key: "12h", label: "12" },
  { key: "24h", label: "24" },
  { key: "overall", label: "Overall" },
];

const currencyText = (value) => {
  const amount = Number(value || 0);
  return `$${amount.toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
};

const percentageText = (value) => {
  if (value === null || value === undefined || value === "") return "-";
  const percentage = Number(value);
  return Number.isFinite(percentage) ? `${percentage.toFixed(2)}%` : "-";
};

const metricHeaderCellSx = {
  ...tableHeaderCellSx,
  px: 0.2,
  py: 0.55,
  textAlign: "center",
  borderLeft: "1px solid #e9ecef",
};

const compactMetricCellSx = {
  ...tableBodyCellSx,
  px: 0.15,
  py: 0.35,
  textAlign: "center",
  fontSize: "10px",
  fontWeight: 400,
  whiteSpace: "nowrap",
  borderLeft: "1px solid #edf2f7",
};

const statsMetricCellSx = {
  ...compactMetricCellSx,
  px: 0,
  fontSize: "12px !important",
  letterSpacing: "-1px",
  overflowWrap: "normal",
  whiteSpace: "nowrap",
};

const listSearchInputSx = {
  minHeight: "24px !important",
  padding: "1px 8px !important",
  "& input": {
    height: "20px !important",
    fontSize: "10px !important",
  },
  "& input[type='date']": {
    lineHeight: "20px !important",
  },
  "& input[type='date']::-webkit-calendar-picker-indicator": {
    width: "12px",
    height: "12px",
    padding: 0,
    margin: 0,
  },
};

const listSearchControlSx = {
  width: { xs: "100%", sm: "9rem" },
  flex: { xs: "1 1 100%", sm: "0 0 9rem" },
};

const listSearchSelectSx = {
  "--soft-select-height": "24px",
  "--soft-select-value-height": "22px",
  "--soft-select-font-size": "10px",
  "--soft-select-value-px": "8px",
  "--soft-select-text-top": "50%",
  "--soft-select-indicator-padding": "3px",
  "--soft-select-indicator-size": "12px",
  "--soft-select-menu-padding": "4px",
  "--soft-select-option-padding": "4px 8px",
  "--soft-select-option-min-height": "24px",
  "--soft-select-option-font-size": "10px",
};

const listExportButtonSx = {
  minHeight: 24,
  height: 24,
  px: 1.25,
  py: 0,
  fontSize: "10px",
  lineHeight: 1,
  textTransform: "none",
  borderRadius: "6px",
  "& .MuiIcon-root": {
    fontSize: "14px !important",
  },
};

function MetricHoverCard({ label, breakdown = emptyBreakdown }) {
  return (
    <SoftBox
      sx={{
        minWidth: 320,
        p: 1,
        color: "#1e293b",
        backgroundColor: "#fde047",
        borderRadius: "6px",
      }}
    >
      <SoftTypography variant="caption" fontWeight="regular" color="dark" sx={{ fontSize: "12px" }}>
        {label}
      </SoftTypography>
      <SoftBox
        sx={{
          display: "grid",
          gridTemplateColumns: "78px repeat(4, minmax(44px, 1fr))",
          mt: 0.75,
          borderTop: "1px solid rgba(15, 23, 42, 0.2)",
          borderLeft: "1px solid rgba(15, 23, 42, 0.2)",
        }}
      >
        {["", ...metricPeriods.map((period) => period.label)].map((heading, index) => (
          <SoftBox
            key={`heading-${heading || index}`}
            sx={{
              px: 0.5,
              py: 0.45,
              textAlign: index === 0 ? "left" : "center",
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {heading}
          </SoftBox>
        ))}
        {[
          { label: "Complete", key: "complete" },
          { label: "Clicks", key: "clicks" },
          { label: "V.RTR", key: "v_rtr" },
        ].flatMap((row) => [
          <SoftBox
            key={`${row.key}-label`}
            sx={{
              px: 0.5,
              py: 0.45,
              borderRight: "1px solid rgba(15, 23, 42, 0.2)",
              borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
              fontSize: "12px",
              fontWeight: 400,
            }}
          >
            {row.label}
          </SoftBox>,
          ...metricPeriods.map((period) => (
            <SoftBox
              key={`${row.key}-${period.key}`}
              sx={{
                px: 0.5,
                py: 0.45,
                textAlign: "center",
                borderRight: "1px solid rgba(15, 23, 42, 0.2)",
                borderBottom: "1px solid rgba(15, 23, 42, 0.2)",
                fontSize: "10px",
                fontWeight: 400,
              }}
            >
              {breakdown?.[period.key]?.[row.key] ?? "-"}
            </SoftBox>
          )),
        ])}
      </SoftBox>
    </SoftBox>
  );
}

MetricHoverCard.propTypes = {
  label: PropTypes.string.isRequired,
  breakdown: PropTypes.object,
};

function ProjectsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const { permissions = [], userPermission = [] } = useAuth();
  const projectPermissions = useProjectPermissions(permissions, userPermission);
  const { canUseProjectLiveChat, openProjectLiveChat } = useProjectLiveChat();
  const token = localStorage.getItem("authToken");
  const initialProjectFilters = projectFilterStateFromSearch(location.search);
  const canViewProjects = projectPermissions.canViewPage;
  const canSearchProjects = projectPermissions.canAction("search");
  const canExportProjects = projectPermissions.canAction("export");
  const canUseActions = projectPermissions.canField("actions");
  const canExpandDetails = projectPermissions.canAction("expand_details");
  const canSetProjectStatus = projectPermissions.canSetProjectStatus;
  const allowedProjectStatusActions = projectStatusActions.filter((action) =>
    canSetProjectStatus(action.value)
  );
  const canOpenProjectDetails = projectPermissions.canAction("open_project_details");
  const canSubmitProjectDetails = projectPermissions.canAction("submit_project_details");
  const canManageProjectLinkHitGap = projectPermissions.canAction(
    "manage_project_link_hit_gap"
  );
  const canViewProjectCode = projectPermissions.canField("project_code");
  const canViewProjectTitle = projectPermissions.canField("project_title");
  const canViewClient = projectPermissions.canField("client");
  const canViewProjectManagerTeam = projectPermissions.canField("project_manager_team");
  const canViewProjectManager = projectPermissions.canField("project_manager");
  const canViewIr = projectPermissions.canField("ir");
  const canViewLoi = projectPermissions.canField("loi");
  const canViewStudyType = projectPermissions.canField("study_type");
  const canViewProjectType = projectPermissions.canField("project_type");
  const canViewCpi = projectPermissions.canField("cpi");
  const canViewOverallQuota = projectPermissions.canField("overall_quota");
  const canViewRequiredN = projectPermissions.canField("required_n");
  const canViewProjectLaunchDate = projectPermissions.canField("project_launch_date");
  const canViewComments = projectPermissions.canField("comments");
  const canViewCountry = projectPermissions.canField("country");
  const canViewStatus = projectPermissions.canField("status");
  const canViewStats = projectPermissions.canField("stats");
  const canViewCompleteDeviceBrowser = projectPermissions.canField("complete_device_browser_counting");
  const canViewInternalUserMetrics = projectPermissions.canField("internal_user_metrics");
  const canViewRtr = projectPermissions.canField("rtr");
  const canViewRevenue = projectPermissions.canField("revenue");
  const canViewCreatedDate = projectPermissions.canField("created_at");

  const [projects, setProjects] = useState([]);
  const [counts, setCounts] = useState({});
  const [activeTab, setActiveTab] = useState(() => projectTabFromSearch(location.search));
  const isRecentlyActive = activeTab === "live" && recentlyActiveFromSearch(location.search);
  const showEpc = canViewRtr && activeTab !== "won";
  const [loading, setLoading] = useState(true);
  const [statusLoading, setStatusLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(initialProjectFilters.search);
  const [searchQuery, setSearchQuery] = useState(initialProjectFilters.search);
  const [listStartDate, setListStartDate] = useState(initialProjectFilters.startDate);
  const [listEndDate, setListEndDate] = useState(initialProjectFilters.endDate);
  const [studyTypeFilter, setStudyTypeFilter] = useState(initialProjectFilters.studyType);
  const [projectTypeFilter, setProjectTypeFilter] = useState(initialProjectFilters.projectType);
  const [clientFilter, setClientFilter] = useState(initialProjectFilters.client);
  const [projectFilterClients, setProjectFilterClients] = useState([]);
  const [projectFilterClientsLoading, setProjectFilterClientsLoading] = useState(false);
  const [projectFilterStudyTypes, setProjectFilterStudyTypes] = useState([]);
  const [projectFilterProjectTypes, setProjectFilterProjectTypes] = useState([]);
  const [projectFilterTypesLoading, setProjectFilterTypesLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInput, setPageInput] = useState("1");
  const [pagination, setPagination] = useState({ from: 0, to: 0, total: 0, last_page: 1 });
  const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
  const [actionsAnchor, setActionsAnchor] = useState(null);
  const [actionsProject, setActionsProject] = useState(null);
  const [invoiceAtZeroProject, setInvoiceAtZeroProject] = useState(null);
  const [metricVendors, setMetricVendors] = useState([]);
  const [expandedProjectIds, setExpandedProjectIds] = useState([]);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);
  const [quotaDialogProject, setQuotaDialogProject] = useState(null);
  const [pendingLaunchStatus, setPendingLaunchStatus] = useState(null);
  const [quotaForm, setQuotaForm] = useState({
    client: null,
    project_manager_role: null,
    project_manager: null,
    project_type: null,
    project_type_option: null,
    project_title: "",
    overall_quota: "",
    overall_clicks_quota: "",
    link_hit_gap_seconds: "",
    is_verisoul_security: false,
    required_n: "",
    ir: "",
    loi: "",
    won_cpi: "",
    project_launch_date: "",
    comments: "",
  });
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [clientSearch, setClientSearch] = useState("");
  const [projectManagerRoles, setProjectManagerRoles] = useState([]);
  const [projectManagersLoading, setProjectManagersLoading] = useState(false);
  const [projectTypes, setProjectTypes] = useState([]);
  const [projectTypesLoading, setProjectTypesLoading] = useState(false);
  const [bidProjectTypeOptions, setBidProjectTypeOptions] = useState([]);
  const [bidProjectTypeOptionsLoading, setBidProjectTypeOptionsLoading] = useState(false);
  const [quotaSubmitLoading, setQuotaSubmitLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", color: "success" });
  const [verisoulResult, setVerisoulResult] = useState(null);
  const [communicationDialog, setCommunicationDialog] = useState(null);

  useEffect(() => {
    setPageInput(String(currentPage || 1));
  }, [currentPage]);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
      "Content-Type": "application/json",
    }),
    [token]
  );

  const visibleProjectTabs = useMemo(
    () => projectTabs.filter((tab) => projectPermissions.canTab(tab.value)),
    [projectPermissions]
  );

  const firstVisibleProjectTab = visibleProjectTabs[0]?.value || "won";

  const showMessage = useCallback((message, color = "success") => {
    setSnackbar({ open: true, message, color });
  }, []);

  const projectFilterVisibility = useMemo(
    () => ({
      canSearch: canSearchProjects,
      canViewCreatedDate,
      canViewStudyType,
      canViewProjectType,
      canViewClient,
    }),
    [canSearchProjects, canViewClient, canViewCreatedDate, canViewProjectType, canViewStudyType]
  );

  const currentProjectFilters = useMemo(
    () => ({
      search: searchQuery,
      startDate: listStartDate,
      endDate: listEndDate,
      studyType: studyTypeFilter,
      projectType: projectTypeFilter,
      client: clientFilter,
    }),
    [clientFilter, listEndDate, listStartDate, projectTypeFilter, searchQuery, studyTypeFilter]
  );

  const currentProjectFilterSuffix = useMemo(
    () => projectFilterSearchSuffix(currentProjectFilters, projectFilterVisibility),
    [currentProjectFilters, projectFilterVisibility]
  );

  const openProjectCommunicationDialog = useCallback((type, project) => {
    setCommunicationDialog({
      type,
      scope: {
        scopeType: "project",
        projectId: project.id,
        projectCode: project.quest_code || project.project_code || project.id,
        projectName: project.project_title || project.project_name || project.quest_code,
        label: `Project: ${project.quest_code || project.project_code || project.id}`,
      },
    });
  }, []);

  const updateProjectTabInUrl = useCallback(
    (tab) => {
      const params = new URLSearchParams(location.search);
      params.set("tab", tab);
      if (tab !== "live") params.delete("recent_active");
      writeProjectFiltersToParams(params, currentProjectFilters, projectFilterVisibility);
      navigate(
        {
          pathname: location.pathname,
          search: `?${params.toString()}`,
        },
        { replace: true }
      );
    },
    [currentProjectFilters, location.pathname, location.search, navigate, projectFilterVisibility]
  );

  const clearRecentlyActiveFilter = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete("recent_active");
    params.set("tab", "live");
    writeProjectFiltersToParams(params, currentProjectFilters, projectFilterVisibility);
    navigate(
      {
        pathname: location.pathname,
        search: `?${params.toString()}`,
      },
      { replace: true }
    );
    setCurrentPage(1);
  }, [currentProjectFilters, location.pathname, location.search, navigate, projectFilterVisibility]);

  const fetchClients = useCallback(
    async (search = "") => {
      if (!token) return;

      try {
        setClientsLoading(true);
        const params = new URLSearchParams({ limit: "500" });
        if (String(search || "").trim()) params.set("q", String(search).trim());

        const response = await fetch(`${API_URL}/projects/clients?${params.toString()}`, {
          headers: authHeaders,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Failed to fetch clients");
        }

        setClients((Array.isArray(data.clients) ? data.clients : []).map(clientToSelectOption).filter(Boolean));
      } catch (error) {
        showMessage(error.message || "Failed to fetch clients", "error");
      } finally {
        setClientsLoading(false);
      }
    },
    [authHeaders, showMessage, token]
  );

  const fetchProjectFilterClients = useCallback(async () => {
    if (!token || !canViewClient) {
      setProjectFilterClients([]);
      return;
    }

    try {
      setProjectFilterClientsLoading(true);
      const params = new URLSearchParams({
        project_filter: "1",
        project_tab: activeTab,
      });
      const response = await fetch(`${API_URL}/projects/clients?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch project client filters");
      }

      setProjectFilterClients(
        (Array.isArray(data.clients) ? data.clients : []).map(clientToSelectOption).filter(Boolean)
      );
    } catch (error) {
      setProjectFilterClients([]);
      showMessage(error.message || "Failed to fetch project client filters", "error");
    } finally {
      setProjectFilterClientsLoading(false);
    }
  }, [activeTab, authHeaders, canViewClient, showMessage, token]);

  const fetchProjectFilterTypes = useCallback(async () => {
    if (!token || (!canViewStudyType && !canViewProjectType)) {
      setProjectFilterStudyTypes([]);
      setProjectFilterProjectTypes([]);
      return;
    }

    try {
      setProjectFilterTypesLoading(true);
      const params = new URLSearchParams({
        project_filter: "1",
        project_tab: activeTab,
      });
      const requests = [
        canViewStudyType
          ? fetch(`${API_URL}/projects/project-types?${params.toString()}`, { headers: authHeaders })
          : Promise.resolve(null),
        canViewProjectType
          ? fetch(`${API_URL}/projects/project-type-options?${params.toString()}`, {
              headers: authHeaders,
            })
          : Promise.resolve(null),
      ];
      const [studyTypeResponse, projectTypeResponse] = await Promise.all(requests);
      const [studyTypeData, projectTypeData] = await Promise.all([
        studyTypeResponse?.json().catch(() => ({})) || {},
        projectTypeResponse?.json().catch(() => ({})) || {},
      ]);

      if (studyTypeResponse && (!studyTypeResponse.ok || studyTypeData.success !== 1)) {
        throw new Error(studyTypeData.message || "Failed to fetch study type filters");
      }
      if (projectTypeResponse && (!projectTypeResponse.ok || projectTypeData.success !== 1)) {
        throw new Error(projectTypeData.message || "Failed to fetch project type filters");
      }

      setProjectFilterStudyTypes(
        (Array.isArray(studyTypeData.project_types) ? studyTypeData.project_types : [])
          .map(toSelectOption)
          .filter(Boolean)
      );
      setProjectFilterProjectTypes(
        (Array.isArray(projectTypeData.project_type_options)
          ? projectTypeData.project_type_options
          : []
        )
          .map(toSelectOption)
          .filter(Boolean)
      );
    } catch (error) {
      setProjectFilterStudyTypes([]);
      setProjectFilterProjectTypes([]);
      showMessage(error.message || "Failed to fetch project filter options", "error");
    } finally {
      setProjectFilterTypesLoading(false);
    }
  }, [activeTab, authHeaders, canViewProjectType, canViewStudyType, showMessage, token]);

  const fetchProjectManagers = useCallback(async () => {
    if (!token) return;

    try {
      setProjectManagersLoading(true);
      const response = await fetch(`${API_URL}/projects/project-managers`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch Project Management Team");
      }

      setProjectManagerRoles(
        (Array.isArray(data.roles) ? data.roles : []).map(toSelectOption).filter(Boolean)
      );
    } catch (error) {
      showMessage(error.message || "Failed to fetch Project Management Team", "error");
    } finally {
      setProjectManagersLoading(false);
    }
  }, [authHeaders, showMessage, token]);

  const fetchProjectTypes = useCallback(async () => {
    if (!token) return;

    try {
      setProjectTypesLoading(true);
      const response = await fetch(`${API_URL}/projects/project-types`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch study types");
      }

      setProjectTypes(
        (Array.isArray(data.project_types) ? data.project_types : []).map(toSelectOption).filter(Boolean)
      );
    } catch (error) {
      showMessage(error.message || "Failed to fetch study types", "error");
    } finally {
      setProjectTypesLoading(false);
    }
  }, [authHeaders, showMessage, token]);

  const fetchBidProjectTypeOptions = useCallback(async () => {
    if (!token) return;

    try {
      setBidProjectTypeOptionsLoading(true);
      const response = await fetch(`${API_URL}/projects/project-type-options`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch project types");
      }

      setBidProjectTypeOptions(
        (Array.isArray(data.project_type_options) ? data.project_type_options : [])
          .map(toSelectOption)
          .filter(Boolean)
      );
    } catch (error) {
      showMessage(error.message || "Failed to fetch project types", "error");
    } finally {
      setBidProjectTypeOptionsLoading(false);
    }
  }, [authHeaders, showMessage, token]);

  const buildProjectListParams = useCallback(
    (page = 1) => {
      const params = new URLSearchParams({
        project_tab: activeTab,
        page: String(page),
        per_page: "50",
      });
      if (canSearchProjects && searchQuery.trim()) params.set("q", searchQuery.trim());
      if (canViewCreatedDate && listStartDate) params.set("created_from", listStartDate);
      if (canViewCreatedDate && listEndDate) params.set("created_to", listEndDate);
      if (canViewStudyType && studyTypeFilter.length) {
        studyTypeFilter.forEach((option) =>
          params.append("study_type_ids[]", String(selectOptionValue(option)))
        );
      }
      if (canViewProjectType && projectTypeFilter.length) {
        projectTypeFilter.forEach((option) =>
          params.append("project_type_option_ids[]", String(selectOptionValue(option)))
        );
      }
      if (canViewClient && clientFilter.length) {
        clientFilter.forEach((option) =>
          params.append("client_ids[]", String(selectOptionValue(option)))
        );
      }
      if (isRecentlyActive) params.set("recent_active", "1");

      return params;
    },
    [
      activeTab,
      canSearchProjects,
      canViewClient,
      canViewCreatedDate,
      canViewProjectType,
      canViewStudyType,
      clientFilter,
      listEndDate,
      listStartDate,
      projectTypeFilter,
      searchQuery,
      studyTypeFilter,
      isRecentlyActive,
    ]
  );

  const fetchProjects = useCallback(async () => {
    if (!canViewProjects || !token) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const params = buildProjectListParams(currentPage);
      const response = await fetch(`${API_URL}/projects?${params.toString()}`, {
        headers: authHeaders,
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to fetch projects");
      }

      const page = data.bids || {};
      setProjects(Array.isArray(page.data) ? page.data : []);
      setCounts(data.project_counts || {});
      setMetricVendors(Array.isArray(data.project_metric_vendors) ? data.project_metric_vendors : []);
      setPagination({
        from: page.from || 0,
        to: page.to || 0,
        total: page.total || 0,
        last_page: page.last_page || 1,
      });
    } catch (error) {
      showMessage(error.message || "Failed to fetch projects", "error");
    } finally {
      setLoading(false);
    }
  }, [
    activeTab,
    authHeaders,
    buildProjectListParams,
    canViewProjects,
    currentPage,
    showMessage,
    token,
  ]);

  const projectManagerUsersForRole = useCallback(
    (roleOption) => {
      const roleValue = selectOptionValue(roleOption);
      if (!roleValue) return [];

      const selectedRole = [...projectManagerRoles, roleOption].find(
        (role) => String(selectOptionValue(role)) === String(roleValue)
      );

      return (Array.isArray(selectedRole?.users) ? selectedRole.users : [])
        .map(toSelectOption)
        .filter(Boolean);
    },
    [projectManagerRoles]
  );

  const quotaProjectManagerUsers = useMemo(
    () => projectManagerUsersForRole(quotaForm.project_manager_role),
    [projectManagerUsersForRole, quotaForm.project_manager_role]
  );
  const quotaProjectManagerRoleSelected = Boolean(
    selectOptionValue(quotaForm.project_manager_role)
  );

  useEffect(() => {
    if (!canSearchProjects) {
      setSearchQuery("");
      return undefined;
    }

    const timeout = setTimeout(() => {
      setSearchQuery(searchInput);
      setCurrentPage(1);
    }, 500);

    return () => clearTimeout(timeout);
  }, [canSearchProjects, searchInput]);

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    params.set("tab", activeTab);
    writeProjectFiltersToParams(params, currentProjectFilters, projectFilterVisibility);

    const nextSearch = params.toString();
    const currentSearch = new URLSearchParams(location.search).toString();

    if (nextSearch === currentSearch) return;

    navigate(
      {
        pathname: location.pathname,
        search: `?${nextSearch}`,
      },
      { replace: true }
    );
  }, [
    activeTab,
    currentProjectFilters,
    location.pathname,
    location.search,
    navigate,
    projectFilterVisibility,
  ]);

  useEffect(() => {
    const tabFromUrl = projectTabFromSearch(location.search);
    const nextTab = visibleProjectTabs.some((tab) => tab.value === tabFromUrl)
      ? tabFromUrl
      : firstVisibleProjectTab;
    if (nextTab === activeTab) return;

    setActiveTab(nextTab);
    setCurrentPage(1);
  }, [activeTab, firstVisibleProjectTab, location.search, visibleProjectTabs]);

  useEffect(() => {
    if (!quotaDialogOpen) return;

    fetchClients(clientSearch);
    fetchProjectManagers();
    fetchProjectTypes();
    fetchBidProjectTypeOptions();
  }, [
    clientSearch,
    fetchBidProjectTypeOptions,
    fetchClients,
    fetchProjectManagers,
    fetchProjectTypes,
    quotaDialogOpen,
  ]);

  useEffect(() => {
    fetchProjectFilterClients();
  }, [fetchProjectFilterClients]);

  useEffect(() => {
    fetchProjectFilterTypes();
  }, [fetchProjectFilterTypes]);

  const openActionsMenu = (event, project) => {
    if (!canUseActions || allowedProjectStatusActions.length === 0) return;
    setActionsAnchor(event.currentTarget);
    setActionsProject(project);
  };

  const closeActionsMenu = () => {
    if (statusLoading) return;
    setActionsAnchor(null);
    setActionsProject(null);
  };

  const updateProjectStatus = async (project, status) => {
    if (!canSetProjectStatus(status)) return;
    if (!project?.id) return;

    try {
      setStatusLoading(true);
      const response = await fetch(`${API_URL}/projects/${project.id}/status`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify({ status }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to update project status");
      }

      showMessage(data.message || "Project status updated successfully", "success");
      setActionsAnchor(null);
      setActionsProject(null);
      fetchProjects();
      return true;
    } catch (error) {
      showMessage(error.message || "Failed to update project status", "error");
      return false;
    } finally {
      setStatusLoading(false);
    }
  };

  const openProjectQuotaDialog = (project, launchStatus = null) => {
    if (!canOpenProjectDetails) return;
    setClientSearch("");
    setQuotaDialogProject(project);
    setPendingLaunchStatus(launchStatus);
    setQuotaForm({
      client: clientOptionFromProject(project),
      project_manager_role: projectManagerOptionFromProject(project),
      project_manager: projectManagerUserOptionFromProject(project),
      project_type: studyTypeOptionFromProject(project),
      project_type_option: bidProjectTypeOptionFromProject(project),
      project_title: project?.project_title || "",
      overall_quota: project?.overall_quota ?? "",
      overall_clicks_quota: project?.overall_clicks_quota ?? "",
      link_hit_gap_seconds:
        project?.link_hit_gap_seconds === null || project?.link_hit_gap_seconds === undefined
          ? ""
          : String(project.link_hit_gap_seconds),
      is_verisoul_security: Boolean(project?.is_verisoul_security),
      required_n: "",
      ir: "",
      loi: "",
      won_cpi: "",
      project_launch_date: toDatetimeLocalValue(project?.project_launch_date),
      comments: project?.comments || "",
    });
    setQuotaDialogOpen(true);
  };

  const closeProjectQuotaDialog = () => {
    if (quotaSubmitLoading) return;
    setQuotaDialogOpen(false);
    setQuotaDialogProject(null);
    setPendingLaunchStatus(null);
  };

  const handleProjectStatusSelection = (project, status) => {
    if (status === "invoice_at_zero") {
      setActionsAnchor(null);
      setActionsProject(null);
      setInvoiceAtZeroProject(project);
      return;
    }

    if (!liveStatuses.includes(status)) {
      updateProjectStatus(project, status);
      return;
    }

    if (
      !canOpenProjectDetails ||
      !canSubmitProjectDetails ||
      !canViewProjectManagerTeam ||
      !canViewProjectManager
    ) {
      showMessage(
        "Project details and Project Manager permissions are required before launch.",
        "error"
      );
      setActionsAnchor(null);
      setActionsProject(null);
      return;
    }

    setActionsAnchor(null);
    setActionsProject(null);
    openProjectQuotaDialog(project, status);
  };

  const closeInvoiceAtZeroConfirmation = () => {
    if (statusLoading) return;
    setInvoiceAtZeroProject(null);
  };

  const confirmInvoiceAtZero = async () => {
    if (!invoiceAtZeroProject) return;

    const updated = await updateProjectStatus(invoiceAtZeroProject, "invoice_at_zero");
    if (updated) setInvoiceAtZeroProject(null);
  };

  const updateQuotaForm = (field, value) => {
    setQuotaForm((current) => {
      const nextValue = (() => {
        if (["overall_quota", "overall_clicks_quota", "required_n"].includes(field)) {
          return digitsOnly(value, 7);
        }
        if (field === "link_hit_gap_seconds") return digitsOnly(value, 5);
        if (["ir", "loi", "won_cpi"].includes(field)) return decimalOnly(value, 7, 2);
        return value;
      })();

      if (field === "project_manager_role") {
        return {
          ...current,
          project_manager_role: nextValue,
          project_manager: null,
        };
      }

      return {
        ...current,
        [field]: nextValue,
      };
    });
  };

  const submitProjectQuota = async () => {
    if (!canSubmitProjectDetails) return;
    if (!quotaDialogProject?.id) return;

    const rawGapSeconds = String(quotaForm.link_hit_gap_seconds ?? "").trim();
    const gapSeconds = rawGapSeconds === "" ? null : Number(rawGapSeconds);
    if (
      activeTab === "live" &&
      canManageProjectLinkHitGap &&
      gapSeconds !== null &&
      (!Number.isInteger(gapSeconds) || gapSeconds < 1 || gapSeconds > 86400)
    ) {
      showMessage(
        "Link hit gap must be NULL or a whole number between 1 and 86400 seconds.",
        "error"
      );
      return;
    }

    try {
      setQuotaSubmitLoading(true);
      const overallQuota =
        quotaForm.overall_quota === "" ? null : Number(quotaForm.overall_quota || 0);
      const overallClicksQuota =
        quotaForm.overall_clicks_quota === ""
          ? null
          : Number(quotaForm.overall_clicks_quota || 0);
      const basePayload = {
        project_title: quotaForm.project_title || "",
        overall_quota: overallQuota && Number.isFinite(overallQuota) ? overallQuota : null,
        overall_clicks_quota:
          overallClicksQuota && Number.isFinite(overallClicksQuota) ? overallClicksQuota : null,
        project_launch_date: quotaForm.project_launch_date || null,
        comments: quotaForm.comments || "",
      };
      const payload = projectPermissions.filterPayloadByFields(basePayload, {
        project_title: "project_title",
        overall_quota: "overall_quota",
        overall_clicks_quota: "overall_clicks_quota",
        project_launch_date: "project_launch_date",
        comments: "comments",
      });
      if (activeTab === "live" && canManageProjectLinkHitGap) {
        payload.link_hit_gap_seconds = gapSeconds;
      }
      // Only submit this scope action when the checkbox actually changed;
      // editing another project field must not reset child overrides.
      if (
        activeTab === "live" &&
        Boolean(quotaForm.is_verisoul_security) !==
          Boolean(quotaDialogProject?.is_verisoul_security)
      ) {
        payload.is_verisoul_security = Boolean(quotaForm.is_verisoul_security);
      }

      const clientId = selectOptionValue(quotaForm.client);
      const projectManagerRoleId = selectOptionValue(quotaForm.project_manager_role);
      const projectManagerId = selectOptionValue(quotaForm.project_manager);
      const studyTypeId = selectOptionValue(quotaForm.project_type);
      const projectTypeOptionId = selectOptionValue(quotaForm.project_type_option);

      if (canViewProjectManagerTeam && !projectManagerRoleId) {
        showMessage("Project Management Team is required.", "error");
        return;
      }

      if (canViewProjectManager && !projectManagerId) {
        showMessage("Project Manager is required.", "error");
        return;
      }

      if (clientId && canViewClient) payload.client_id = clientId;
      if (projectManagerRoleId && canViewProjectManagerTeam) {
        payload.project_manager_role_id = projectManagerRoleId;
      }
      if (projectManagerId && canViewProjectManager) {
        payload.project_manager_id = projectManagerId;
      }
      if (studyTypeId && canViewStudyType) payload.project_type_id = studyTypeId;
      if (projectTypeOptionId && canViewProjectType) {
        payload.project_type_option_id = projectTypeOptionId;
      }

      const response = await fetch(`${API_URL}/projects/${quotaDialogProject.id}/project-quota`, {
        method: "PATCH",
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success !== 1) {
        throw new Error(data.message || "Failed to update project quota");
      }

      if (data.verisoul_result) {
        setVerisoulResult(data.verisoul_result);
      }

      const projectToLaunch = quotaDialogProject;
      const launchStatus = pendingLaunchStatus;
      setQuotaDialogOpen(false);
      setQuotaDialogProject(null);
      setPendingLaunchStatus(null);

      if (launchStatus) {
        await updateProjectStatus(projectToLaunch, launchStatus);
      } else {
        showMessage(
          data.verisoul_result
            ? "Project details and Verisoul Security updated successfully."
            : data.message || "Project quota updated successfully",
          "success"
        );
        fetchProjects();
      }
    } catch (error) {
      showMessage(error.message || "Failed to update project quota", "error");
    } finally {
      setQuotaSubmitLoading(false);
    }
  };

  const handlePreviousPage = () => {
    setCurrentPage((page) => Math.max(1, Number(page || 1) - 1));
  };

  const handleNextPage = () => {
    setCurrentPage((page) => Math.min(pagination.last_page || 1, Number(page || 1) + 1));
  };

  const openProjectPage = (value) => {
    const page = Math.max(1, Math.min(pagination.last_page || 1, Number(value) || 1));
    setPageInput(String(page));
    setCurrentPage(page);
  };

  const metricVendorColumns = useMemo(
    () =>
      Array.from({ length: 4 }, (_, index) => {
        const vendor = metricVendors[index];
        return vendor || { id: `empty-${index}`, name: "Internal Vendor", initial: "-" };
      }),
    [metricVendors]
  );
  const studyTypeFilterOptions = useMemo(
    () => mergeSelectOptions(projectFilterStudyTypes, studyTypeFilter),
    [projectFilterStudyTypes, studyTypeFilter]
  );
  const projectTypeFilterOptions = useMemo(
    () => mergeSelectOptions(projectFilterProjectTypes, projectTypeFilter),
    [projectFilterProjectTypes, projectTypeFilter]
  );
  const clientFilterOptions = useMemo(
    () => mergeSelectOptions(projectFilterClients, clientFilter),
    [clientFilter, projectFilterClients]
  );

  const toggleProjectDetails = (projectId) => {
    setExpandedProjectIds((current) =>
      current.includes(projectId)
        ? current.filter((id) => id !== projectId)
        : [...current, projectId]
    );
  };

  const metricSourceForVendor = (project, vendor) => {
    const internalVendors = project.project_metrics?.internal_vendors || [];
    return (
      internalVendors.find((item) => String(item.id) === String(vendor.id)) || {
        count: 0,
        breakdown: emptyBreakdown,
      }
    );
  };

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === "asc" ? "desc" : "asc",
    }));
  }, []);

  const sortedProjects = useMemo(
    () =>
      sortTableRows(projects, sortConfig, (project, key) => {
        const metrics = project.project_metrics || {};
        const stats = metrics.stats || {};

        if (key.startsWith("internal_vendor:")) {
          const vendorId = key.split(":")[1];
          return Number(
            (metrics.internal_vendors || []).find(
              (vendor) => String(vendor.id) === String(vendorId)
            )?.count || 0
          );
        }

        switch (key) {
          case "project_code":
            return project.quest_code;
          case "project_name":
            return project.project_title;
          case "client":
            return clientText(project);
          case "manager":
            return activeTab === "won"
              ? project.project_manager_text
              : project.project_manager?.name || project.project_manager?.email;
          case "required_n":
            return projectRequiredNValue(project);
          case "ir":
            return numericValue(metrics.average_ir) ?? numericValue(projectConfiguredIr(project));
          case "loi":
            return numericValue(metrics.average_loi) ?? numericValue(projectConfiguredLoi(project));
          case "study_type":
            return studyTypeText(project);
          case "project_type":
            return projectTypeText(project);
          case "cpi": {
            if (activeTab === "won") return projectConfiguredCpi(project);
            const clientRevenue = numericValue(metrics.client_revenue);
            const completes = numericValue(stats.complete);
            return clientRevenue !== null && completes !== null && completes > 0
              ? clientRevenue / completes
              : numericValue(metrics.calculated_cpi);
          }
          case "device_browser":
            return Number(stats.complete || 0);
          case "complete":
            return Number(stats.complete || 0);
          case "complete_24h":
            return Number(metrics.overall_breakdown?.["24h"]?.complete || 0);
          case "terminate":
            return Number(stats.terminate || 0);
          case "quota_full":
            return Number(stats.quota_full || 0);
          case "security_terminate":
            return Number(stats.security_terminate || 0);
          case "initiate":
            return Number(stats.initiate || 0);
          case "external_vendors":
            return Number(metrics.vendors?.count || 0);
          case "vm":
            return numericValue(metrics.rtr);
          case "epc":
            return epcSortValue(metrics.revenue, metrics.overall_breakdown, stats);
          case "revenue":
            return numericValue(metrics.revenue);
          case "status":
            return project.project_status_label || project.status;
          default:
            return null;
        }
      }),
    [activeTab, projects, sortConfig]
  );

  const handleExportProjects = async () => {
    if (!canExportProjects || exportLoading) return;

    try {
      setExportLoading(true);
      const exportProjects = [];
      let exportPage = 1;
      let lastPage = 1;

      do {
        const params = buildProjectListParams(exportPage);
        const response = await fetch(`${API_URL}/projects?${params.toString()}`, {
          headers: authHeaders,
        });
        const data = await response.json().catch(() => ({}));

        if (!response.ok || data.success !== 1) {
          throw new Error(data.message || "Failed to fetch projects for export");
        }

        const page = data.bids || {};
        exportProjects.push(...(Array.isArray(page.data) ? page.data : []));
        lastPage = Math.max(1, Number(page.last_page || 1));
        exportPage += 1;
      } while (exportPage <= lastPage);

      if (!exportProjects.length) {
        showMessage("No project data available to export.", "error");
        return;
      }

      const commonColumns = [
        {
          label: activeTab === "won" ? "Project Code" : "Project Code / Client",
          value: (project) =>
            activeTab === "won" || !canViewClient
              ? textOrDash(project.quest_code)
              : `${textOrDash(project.quest_code)} / ${textOrDash(clientText(project))}`,
        },
        canViewProjectTitle
          ? { label: "Project Name", value: (project) => textOrDash(project.project_title) }
          : null,
        activeTab === "won" && canViewClient
          ? { label: "Client Name", value: (project) => textOrDash(clientText(project)) }
          : null,
        activeTab === "won"
          ? canViewProjectManagerTeam
            ? {
                label: "Project Management Team",
                value: (project) => textOrDash(project.project_manager_text),
              }
            : null
          : canViewProjectManager
            ? {
                label: "Project Manager",
                value: (project) =>
                  textOrDash(project.project_manager?.name || project.project_manager?.email),
              }
            : null,
        canViewRequiredN
          ? { label: "Required N", value: (project) => projectRequiredNText(project) }
          : null,
        canViewIr
          ? {
              label: "IR(%)",
              value: (project) =>
                `${formatIrDisplay(project.project_metrics?.average_ir)}/${formatIrDisplay(
                  projectConfiguredIr(project)
                )}`,
            }
          : null,
        canViewLoi
          ? {
              label: "LOI(mins)",
              value: (project) =>
                `${roundedMetricText(project.project_metrics?.average_loi)}/${roundedMetricText(
                  projectConfiguredLoi(project)
                )}`,
            }
          : null,
        canViewStudyType
          ? { label: "Study Type", value: (project) => textOrDash(studyTypeText(project)) }
          : null,
        canViewProjectType
          ? { label: "Project Type", value: (project) => textOrDash(projectTypeText(project)) }
          : null,
        canViewCpi
          ? {
              label: "CPI",
              value: (project) =>
                activeTab === "won" ? wonCpiText(project) : calculatedCpiText(project),
            }
          : null,
      ].filter(Boolean);

      const operationalColumns =
        activeTab === "won"
          ? []
          : [
              ...(canViewStats
                ? [
                    {
                      label: "c/QS",
                      value: (project) =>
                        `${Number(project.project_metrics?.stats?.complete || 0)}/${quotaText(
                          project.overall_quota
                        )}`,
                    },
                    {
                      label: "T",
                      value: (project) =>
                        Number(project.project_metrics?.stats?.terminate || 0),
                    },
                    {
                      label: "OQ",
                      value: (project) =>
                        Number(project.project_metrics?.stats?.quota_full || 0),
                    },
                    {
                      label: "ST",
                      value: (project) =>
                        Number(project.project_metrics?.stats?.security_terminate || 0),
                    },
                    {
                      label: "P",
                      value: (project) =>
                        Number(project.project_metrics?.stats?.initiate || 0),
                    },
                  ]
                : []),
              ...(canViewInternalUserMetrics
                ? metricVendorColumns.map((vendor, index) => ({
                    label: `${vendor.name || "Internal Vendor"}${
                      vendor.initial && vendor.initial !== "-" ? ` (${vendor.initial})` : ` ${index + 1}`
                    }`,
                    value: (project) => Number(metricSourceForVendor(project, vendor).count || 0),
                  }))
                : []),
              ...(canViewInternalUserMetrics
                ? [
                    {
                      label: "External Vendors",
                      value: (project) =>
                        Number(project.project_metrics?.vendors?.count || 0),
                    },
                  ]
                : []),
              ...(canViewRtr
                ? [
                    {
                      label: "V.M",
                      value: (project) => percentageText(project.project_metrics?.rtr),
                    },
                  ]
                : []),
              ...(showEpc
                ? [
                    {
                      label: "EPC",
                      value: (project) =>
                        epcText(
                          project.project_metrics?.revenue,
                          project.project_metrics?.overall_breakdown,
                          project.project_metrics?.stats
                        ),
                    },
                  ]
                : []),
              ...(canViewRevenue
                ? [
                    {
                      label: "Revenue",
                      value: (project) => currencyText(project.project_metrics?.revenue),
                    },
                  ]
                : []),
            ];

      const statusColumns = canViewStatus
        ? [
            {
              label: "Status",
              value: (project) =>
                textOrDash(
                  project.project_status_label ||
                    projectStatusActions.find(
                      (action) => action.value === String(project.status || "").toLowerCase()
                    )?.label ||
                    project.status
                ),
            },
          ]
        : [];
      const columns = [...commonColumns, ...operationalColumns, ...statusColumns];
      const headers = columns.map((column) => column.label);
      const rows = exportProjects.map((project) =>
        columns.map((column) => column.value(project))
      );
      const worksheet = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      worksheet["!cols"] = headers.map((header, columnIndex) => ({
        wch: Math.min(
          40,
          Math.max(
            String(header).length + 2,
            ...rows.map((row) => String(row[columnIndex] ?? "").length + 2)
          )
        ),
      }));
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Projects");

      const tabLabel =
        projectTabs.find((tab) => tab.value === activeTab)?.label || activeTab;
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, "");
      XLSX.writeFile(
        workbook,
        `projects-${exportFilenamePart(tabLabel)}-${timestamp}.xlsx`
      );
      showMessage("Projects exported successfully.", "success");
    } catch (error) {
      showMessage(error.message || "Failed to export projects.", "error");
    } finally {
      setExportLoading(false);
    }
  };

  const statusBadge = (project) => {
    const tab = projectTabFromStatus(project.status);
    const style = tabStyles[tab] || tabStyles.won;

    return (
      <SoftBox
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 20,
          px: 0.65,
          borderRadius: "999px",
          color: style.color,
          backgroundColor: style.background,
          border: `1px solid ${style.border}`,
          fontSize: 10,
          fontWeight: 400,
          lineHeight: 1.1,
          whiteSpace: "normal",
          textAlign: "center",
        }}
      >
        {textOrDash(project.project_status_label)}
      </SoftBox>
    );
  };

  const canEditProjectDetails = (project) =>
    canUseActions &&
    canOpenProjectDetails &&
    canSubmitProjectDetails &&
    canViewOverallQuota &&
    activeTab === "live" &&
    liveStatuses.includes(String(project?.status || "").toLowerCase());

  const projectActionButton = (project) => {
    if (!canUseActions || allowedProjectStatusActions.length === 0) return "-";

    return (
      <Tooltip title="Project actions">
        <span>
          <IconButton
            size="small"
            disabled={statusLoading}
            onClick={(event) => openActionsMenu(event, project)}
            sx={{ p: 0.25 }}
          >
            <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  const questCodeButton = (project) => {
    const code = canViewProjectCode ? textOrDash(project.quest_code) : "Open";

    return (
      <Tooltip title={code}>
        <SoftBox
          component="button"
          type="button"
          onClick={() =>
            navigate(
              `/projects/${project.id}/countries?tab=${encodeURIComponent(activeTab)}${currentProjectFilterSuffix}`
            )
          }
          sx={{
            display: "block",
            p: 0,
            color: "#0ea5e9",
            background: "transparent",
            border: 0,
            fontFamily: "inherit",
            fontSize: "10px",
            fontWeight: 400,
            lineHeight: 1.1,
            textAlign: "left",
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
        >
          {code}
        </SoftBox>
      </Tooltip>
    );
  };

  const showProjectOpenColumn = true;
  const showProjectRowActions =
    canUseProjectLiveChat ||
    (canUseActions &&
      (canViewComments ||
        projectPermissions.canAction("mail") ||
        projectPermissions.canAction("chat") ||
        canOpenProjectDetails ||
        canExpandDetails));
  const showProjectStatusActionColumn = canUseActions && allowedProjectStatusActions.length > 0;
  const wonColumnCount = [
    showProjectOpenColumn,
    canViewProjectTitle,
    canViewClient,
    canViewProjectManagerTeam,
    canViewRequiredN,
    canViewIr,
    canViewLoi,
    canViewStudyType,
    canViewProjectType,
    canViewCpi,
    canViewStatus,
    showProjectStatusActionColumn,
  ].filter(Boolean).length;
  const liveColumnCount =
    [
      showProjectOpenColumn,
      canViewProjectTitle,
      canViewProjectManager,
      canViewRequiredN,
      canViewIr,
      canViewLoi,
      activeTab === "live" ? canViewStudyType || canViewProjectType : canViewStudyType,
      activeTab === "live" ? false : canViewProjectType,
       canViewCpi,
       canViewRtr,
       showEpc,
       canViewRevenue,
      canViewStatus,
      showProjectRowActions,
      showProjectStatusActionColumn,
    ].filter(Boolean).length +
    (canViewStats ? (activeTab === "live" ? 6 : 5) : 0) +
    (canViewCompleteDeviceBrowser ? 1 : 0) +
    (canViewInternalUserMetrics ? 5 : 0);

  if (!canViewProjects) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <SoftBox p={3}>
          <SoftTypography variant="h5" fontWeight="bold">
            Projects
          </SoftTypography>
          <SoftTypography variant="body2" color="text" mt={1}>
            You do not have permission to view projects.
          </SoftTypography>
        </SoftBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Projects"
        content={snackbar.message}
        open={snackbar.open}
        close={() => setSnackbar((current) => ({ ...current, open: false }))}
        onClose={() => setSnackbar((current) => ({ ...current, open: false }))}
        time={3000}
      />
      <VerisoulResultDialog result={verisoulResult} onClose={() => setVerisoulResult(null)} />

      <SoftBox px={0} pt="2px" pb={1} mt="-14px">
        <SoftBox display="flex" justifyContent="space-between" alignItems="center" mb="2px">
          <SoftTypography
            variant="h4"
            fontWeight="bold"
            color="dark"
            sx={{ fontSize: "0.875rem" }}
          >
            Projects
          </SoftTypography>
        </SoftBox>

        <Card>
          <SoftBox px="2px" pt="2px">
            <Tabs
              value={activeTab}
              onChange={(_, value) => {
                setActiveTab(value);
                setCurrentPage(1);
                updateProjectTabInUrl(value);
              }}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 30,
                "& .MuiTabs-flexContainer": { gap: 1 },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {visibleProjectTabs.map((tab) => {
                const tabStyle = tabStyles[tab.value] || tabStyles.won;

                return (
                  <Tab
                    key={tab.value}
                    value={tab.value}
                    label={`${tab.label} (${counts[tab.value] || 0})`}
                    sx={{
                      color: `${tabStyle.color} !important`,
                      backgroundColor: `${tabStyle.background} !important`,
                      border: `1px solid ${tabStyle.border}`,
                      borderRadius: "8px",
                      fontWeight: "400 !important",
                      minHeight: 28,
                      px: 2,
                      py: "1px",
                      fontSize: "0.875rem",
                      opacity: "1 !important",
                      textTransform: "none",
                      boxShadow: "none",
                      "&.Mui-selected, &.Mui-selected:hover": {
                        color: "#ffffff !important",
                        backgroundColor: `${tabStyle.color} !important`,
                        borderColor: tabStyle.color,
                        boxShadow: `0 8px 20px ${tabStyle.color}33`,
                      },
                      "&:hover": {
                        backgroundColor: `${tabStyle.background} !important`,
                        borderColor: tabStyle.color,
                      },
                    }}
                  />
                );
              })}
            </Tabs>
          </SoftBox>

          {isRecentlyActive && (
            <SoftBox px="2px" pt="2px" display="flex" alignItems="center" gap={0.75}>
              <SoftTypography variant="caption" color="text">
                Recently Active — last 1 hour
              </SoftTypography>
              <SoftButton
                size="small"
                color="info"
                variant="text"
                onClick={clearRecentlyActiveFilter}
                sx={{ minWidth: "auto", px: 0.5, py: 0 }}
              >
                Clear
              </SoftButton>
            </SoftBox>
          )}

          <SoftBox
            display="flex"
            justifyContent="flex-end"
            alignItems={{ xs: "stretch", sm: "center" }}
            flexDirection={{ xs: "column", sm: "row" }}
            gap={0.75}
            px="2px"
            pt="2px"
            pb="4px"
          >
            {(canSearchProjects ||
              canViewCreatedDate ||
              canViewStudyType ||
              canViewProjectType ||
              canViewClient ||
              canExportProjects) && (
              <SoftBox
                display="flex"
                flexWrap="wrap"
                gap={0.75}
                justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                sx={{ ml: { sm: "auto" }, width: { xs: "100%", sm: "auto" } }}
              >
                {canSearchProjects && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftInput
                      size="small"
                      placeholder="Search..."
                      value={searchInput}
                      onChange={(event) => setSearchInput(event.target.value)}
                      sx={listSearchInputSx}
                    />
                  </SoftBox>
                )}
                {canViewCreatedDate && (
                  <>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={listStartDate}
                        onChange={(event) => {
                          setListStartDate(event.target.value);
                          setCurrentPage(1);
                        }}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "Start Date" }}
                      />
                    </SoftBox>
                    <SoftBox sx={listSearchControlSx}>
                      <SoftInput
                        size="small"
                        type="date"
                        value={listEndDate}
                        onChange={(event) => {
                          setListEndDate(event.target.value);
                          setCurrentPage(1);
                        }}
                        sx={listSearchInputSx}
                        inputProps={{ "aria-label": "End Date" }}
                      />
                    </SoftBox>
                  </>
                )}
                {canViewStudyType && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Study Type"
                      value={studyTypeFilter}
                      options={studyTypeFilterOptions}
                      isLoading={projectFilterTypesLoading}
                      onChange={(value) => {
                        setStudyTypeFilter(value);
                        setCurrentPage(1);
                      }}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canViewProjectType && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Project Type"
                      value={projectTypeFilter}
                      options={projectTypeFilterOptions}
                      isLoading={projectFilterTypesLoading}
                      onChange={(value) => {
                        setProjectTypeFilter(value);
                        setCurrentPage(1);
                      }}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canViewClient && (
                  <SoftBox sx={listSearchControlSx}>
                    <SoftSelect
                      size="small"
                      compactMenu
                      placeholder="Client"
                      value={clientFilter}
                      options={clientFilterOptions}
                      isLoading={projectFilterClientsLoading}
                      onChange={(value) => {
                        setClientFilter(value);
                        setCurrentPage(1);
                      }}
                      isSearchable
                      isClearable
                      isMulti
                      sx={listSearchSelectSx}
                    />
                  </SoftBox>
                )}
                {canExportProjects && (
                  <SoftButton
                    color="info"
                    variant="outlined"
                    onClick={handleExportProjects}
                    disabled={exportLoading || pagination.total === 0}
                    sx={listExportButtonSx}
                  >
                    <Icon sx={{ mr: 0.5 }}>download</Icon>
                    {exportLoading ? "Exporting..." : "Export"}
                  </SoftButton>
                )}
              </SoftBox>
            )}
          </SoftBox>

          <SoftBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={0.75}
            px="2px"
            py="2px"
          >
            <SoftBox color="text" fontSize="12px" fontWeight={400}>
              Showing {pagination.from || 0} to {pagination.to || 0} of {pagination.total || 0} entries
            </SoftBox>
            {pagination.last_page > 1 && (
              <SoftBox display="flex" justifyContent="flex-end" alignItems="center">
                <SoftPagination
                  item
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>first_page</Icon>
                </SoftPagination>
                <SoftPagination
                  item
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_left</Icon>
                </SoftPagination>
                <SoftBox display="flex" alignItems="center" gap={0.4} mx={0.4}>
                  <SoftBox
                    component="input"
                    type="text"
                    value={pageInput}
                    onChange={(event) => setPageInput(event.target.value.replace(/\D/g, ""))}
                    onBlur={() => openProjectPage(pageInput)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") openProjectPage(pageInput);
                    }}
                    inputMode="numeric"
                    pattern="[0-9]*"
                    aria-label="Page number"
                    sx={{
                      width: 42,
                      minWidth: 42,
                      maxWidth: 42,
                      height: 22,
                      fontSize: "10px",
                      fontFamily: "inherit",
                      color: "#344767",
                      lineHeight: "20px",
                      textAlign: "center",
                      boxSizing: "border-box",
                      p: 0,
                      border: "1px solid #d2d6da",
                      borderRadius: "8px",
                      outline: "none",
                      "&:focus": {
                        borderColor: "#17c1e8",
                        boxShadow: "0 0 0 2px rgba(23, 193, 232, 0.18)",
                      },
                    }}
                  />
                  <SoftTypography sx={{ fontSize: "10px", color: "#64748b" }}>
                    / {pagination.last_page}
                  </SoftTypography>
                </SoftBox>
                <SoftPagination
                  item
                  onClick={handleNextPage}
                  disabled={currentPage === pagination.last_page}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>chevron_right</Icon>
                </SoftPagination>
                <SoftPagination
                  item
                  onClick={() => setCurrentPage(pagination.last_page)}
                  disabled={currentPage === pagination.last_page}
                  sx={{ width: 24, minWidth: 24, height: 24, minHeight: 24, p: 0 }}
                >
                  <Icon sx={{ fontWeight: "bold", fontSize: "12px !important" }}>last_page</Icon>
                </SoftPagination>
              </SoftBox>
            )}
          </SoftBox>

          {loading ? (
            <SoftBox display="flex" justifyContent="center" alignItems="center" height="420px">
              <FlagLoader />
              <SoftTypography variant="h6" color="text" ml={2}>
                Loading projects...
              </SoftTypography>
            </SoftBox>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                overflowX: "hidden",
                width: "100%",
                borderTop: "1px solid #edf2f7",
              }}
            >
              <Table size="small" sx={{ width: "100%", tableLayout: "fixed" }}>
                <TableHead sx={{ display: "table-header-group" }}>
                  {activeTab === "won" ? (
                    <TableRow>
                      <TableCell sx={tableHeaderCellSx} width="10%"><ProjectTableSortLabel label="Project Code" sortKey="project_code" sortConfig={sortConfig} onSort={handleSort} /></TableCell>
                      {canViewProjectTitle && <TableCell sx={tableHeaderCellSx} width="13%"><ProjectTableSortLabel label="Project Name" sortKey="project_name" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewClient && <TableCell sx={tableHeaderCellSx} width="14%"><ProjectTableSortLabel label="Client Name" sortKey="client" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewProjectManagerTeam && (
                        <TableCell
                          sx={{ ...tableHeaderCellSx, borderLeft: "1px solid #e9ecef" }}
                          width="13%"
                        >
                          <ProjectTableSortLabel label="Project Management Team" sortKey="manager" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>
                      )}
                      {canViewRequiredN && <TableCell sx={metricHeaderCellSx} width="7%"><ProjectTableSortLabel label="Required N" sortKey="required_n" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewIr && <TableCell sx={metricHeaderCellSx} width="7%"><ProjectTableSortLabel label="IR(%)" sortKey="ir" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewLoi && <TableCell sx={metricHeaderCellSx} width="7%"><ProjectTableSortLabel label="LOI(mins)" sortKey="loi" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewStudyType && <TableCell sx={metricHeaderCellSx} width="10%"><ProjectTableSortLabel label="Study Type" sortKey="study_type" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewProjectType && <TableCell sx={metricHeaderCellSx} width="10%"><ProjectTableSortLabel label="Project Type" sortKey="project_type" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewCpi && <TableCell sx={metricHeaderCellSx} width="8%"><ProjectTableSortLabel label="CPI" sortKey="cpi" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {canViewStatus && <TableCell sx={metricHeaderCellSx} width="11%"><ProjectTableSortLabel label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                      {showProjectStatusActionColumn && (
                        <TableCell
                          sx={{ ...metricHeaderCellSx, px: 0, textAlign: "center" }}
                          width="3%"
                        >
                          <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                        </TableCell>
                      )}
                    </TableRow>
                  ) : (
                    <>
                      <TableRow>
                        <TableCell rowSpan={2} sx={tableHeaderCellSx} width="5%">
                          <ProjectTableSortLabel label="Project Code / Client" sortKey="project_code" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>
                        {canViewProjectTitle && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%">
                          <ProjectTableSortLabel label="Project Name" sortKey="project_name" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>}
                        {canViewProjectManager && <TableCell
                          rowSpan={2}
                          sx={{ ...tableHeaderCellSx, borderLeft: "1px solid #e9ecef" }}
                          width="4%"
                        >
                          <ProjectTableSortLabel label="Project Manager" sortKey="manager" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>}
                        {canViewRequiredN && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%">
                          <ProjectTableSortLabel label="Required N" sortKey="required_n" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>}
                        {canViewIr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.3%"><ProjectTableSortLabel label="IR(%)" sortKey="ir" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewLoi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.3%"><ProjectTableSortLabel label="LOI(mins)" sortKey="loi" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewStudyType && activeTab !== "live" && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4%"><ProjectTableSortLabel label="Study Type" sortKey="study_type" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {(canViewProjectType || (activeTab === "live" && canViewStudyType)) && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="4%"><ProjectTableSortLabel label={canViewProjectType ? "Project Type" : "Study Type"} sortKey={canViewProjectType ? "project_type" : "study_type"} sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewCpi && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%"><ProjectTableSortLabel label="CPI" sortKey="cpi" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewCompleteDeviceBrowser && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="6%"><ProjectTableSortLabel label="Device/Browser" sortKey="device_browser" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewStats && <TableCell
                          colSpan={activeTab === "live" ? 6 : 5}
                          width={activeTab === "live" ? "22.3%" : "18.3%"}
                          sx={{ ...metricHeaderCellSx, backgroundColor: "#fde047", color: "#1e293b" }}
                        >
                          Stats
                        </TableCell>}
                        {canViewInternalUserMetrics && <TableCell
                          colSpan={5}
                          width="18%"
                          sx={{ ...metricHeaderCellSx, backgroundColor: "#fde047", color: "#1e293b" }}
                        >
                          Vendor Live
                        </TableCell>}
                        {canViewRtr && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3%"><ProjectTableSortLabel label="V.M" sortKey="vm" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {showEpc && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.3%"><ProjectTableSortLabel label="EPC" sortKey="epc" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewRevenue && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="3.55%"><ProjectTableSortLabel label="Revenue" sortKey="revenue" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {canViewStatus && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="5%"><ProjectTableSortLabel label="Status" sortKey="status" sortConfig={sortConfig} onSort={handleSort} /></TableCell>}
                        {showProjectRowActions && <TableCell rowSpan={2} sx={metricHeaderCellSx} width="7%">
                          <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.55}>
                            {[
                              canViewComments ? "comment" : null,
                              projectPermissions.canAction("mail") ? "mail" : null,
                              projectPermissions.canAction("chat") ? "chat" : null,
                              canExpandDetails ? "keyboard_arrow_down" : null,
                            ].filter(Boolean).map((icon) => (
                              <Icon key={icon} sx={{ fontSize: "0.85rem !important" }}>
                                {icon}
                              </Icon>
                            ))}
                          </SoftBox>
                        </TableCell>}
                        {showProjectStatusActionColumn && <TableCell
                          rowSpan={2}
                          sx={{ ...metricHeaderCellSx, px: 0, whiteSpace: "nowrap", overflowWrap: "normal" }}
                          width="1.75%"
                        >
                          <Icon sx={{ fontSize: "0.95rem !important" }}>more_vert</Icon>
                        </TableCell>}
                      </TableRow>
                      <TableRow>
                        {canViewStats && [
                          { label: "c/QS", key: "complete", color: "#0ea5e9", width: "3.9%" },
                          ...(activeTab === "live"
                            ? [{ label: "24H", key: "complete_24h", color: "#7c3aed", width: "4%" }]
                            : []),
                          { label: "T", key: "terminate", color: "#ef4444", width: "3.6%" },
                          { label: "OQ", key: "quota_full", color: "#f59e0b", width: "3.6%" },
                          { label: "ST", key: "security_terminate", color: "#ef4444", width: "3.6%" },
                          { label: "P", key: "initiate", color: "#0f766e", width: "3.6%" },
                        ].map((column) => (
                          <TableCell
                            key={`stats-${column.label}`}
                            width={column.width}
                            sx={{
                              ...metricHeaderCellSx,
                              color: column.color,
                              backgroundColor: "#f8fafc",
                            }}
                          >
                            <ProjectTableSortLabel label={column.label} sortKey={column.key} sortConfig={sortConfig} onSort={handleSort} />
                          </TableCell>
                        ))}
                        {canViewInternalUserMetrics && metricVendorColumns.map((vendor, index) => (
                          <TableCell
                            key={`vendor-${vendor.id}-${index}`}
                            title={vendor.name}
                            sx={{
                              ...metricHeaderCellSx,
                              color: "#0f172a",
                              backgroundColor: index % 2 === 0 ? "#e2e8f0" : "#fee2e2",
                            }}
                          >
                            <ProjectTableSortLabel label={vendor.initial} sortKey={`internal_vendor:${vendor.id}`} sortConfig={sortConfig} onSort={handleSort} />
                          </TableCell>
                        ))}
                        {canViewInternalUserMetrics && (
                        <TableCell
                          sx={{
                            ...metricHeaderCellSx,
                            color: "#0284c7",
                            backgroundColor: "#e0f2fe",
                          }}
                        >
                          <ProjectTableSortLabel label="O" sortKey="external_vendors" sortConfig={sortConfig} onSort={handleSort} />
                        </TableCell>
                        )}
                      </TableRow>
                    </>
                  )}
                </TableHead>
                <TableBody>
                  {projects.length === 0 ? (
                    <TableRow>
                        <TableCell
                          colSpan={activeTab === "won" ? wonColumnCount : liveColumnCount}
                          sx={tableBodyCellSx}
                          align="center"
                        >
                        No projects found.
                      </TableCell>
                    </TableRow>
                  ) : (
                    sortedProjects.map((project) => {
                      if (activeTab === "won") {
                        return (
                          <TableRow
                            key={project.id}
                            hover
                            sx={{
                              transition: "background-color 150ms ease",
                              "&:hover": { backgroundColor: "#f8fafc" },
                            }}
                          >
                            <TableCell sx={tableBodyCellSx}>
                              <Tooltip title={textOrDash(project.quest_code)}>
                                <SoftBox component="span">{textOrDash(project.quest_code)}</SoftBox>
                              </Tooltip>
                            </TableCell>
                            {canViewProjectTitle && <TableCell
                              sx={{
                                ...tableBodyCellSx,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Tooltip title={textOrDash(project.project_title)}>
                                <SoftBox component="span">{textOrDash(project.project_title)}</SoftBox>
                              </Tooltip>
                            </TableCell>}
                            {canViewClient && <TableCell sx={tableBodyCellSx}>
                              <Tooltip title={textOrDash(clientText(project))}>
                                <SoftBox component="span">{shortClientText(project)}</SoftBox>
                              </Tooltip>
                            </TableCell>}
                            {canViewProjectManagerTeam && <TableCell
                              sx={{ ...tableBodyCellSx, borderLeft: "1px solid #edf2f7" }}
                            >
                              {textOrDash(project.project_manager_text)}
                            </TableCell>}
                            {canViewRequiredN && (
                              <TableCell sx={compactMetricCellSx}>
                                {projectRequiredNText(project)}
                              </TableCell>
                            )}
                            {canViewIr && <TableCell sx={compactMetricCellSx}>
                              <IrMetric
                                average={project.project_metrics?.average_ir}
                                configured={projectConfiguredIr(project)}
                              />
                            </TableCell>}
                            {canViewLoi && <TableCell sx={compactMetricCellSx}>
                              <LoiMetric
                                average={project.project_metrics?.average_loi}
                                configured={projectConfiguredLoi(project)}
                              />
                            </TableCell>}
                            {canViewStudyType && <TableCell sx={compactMetricCellSx}>
                              {textOrDash(studyTypeText(project))}
                            </TableCell>}
                            {canViewProjectType && <TableCell sx={compactMetricCellSx}>
                              {textOrDash(projectTypeText(project))}
                            </TableCell>}
                            {canViewCpi && <TableCell sx={compactMetricCellSx}>{wonCpiText(project)}</TableCell>}
                            {canViewStatus && <TableCell sx={compactMetricCellSx}>{statusBadge(project)}</TableCell>}
                            {showProjectStatusActionColumn && (
                              <TableCell sx={{ ...compactMetricCellSx, px: 0 }}>
                                {projectActionButton(project)}
                              </TableCell>
                            )}
                          </TableRow>
                        );
                      }

                      const metrics = project.project_metrics || {};
                      const stats = metrics.stats || {};
                      const vendors = metrics.vendors || { count: 0, breakdown: emptyBreakdown };
                      const isExpanded = expandedProjectIds.includes(project.id);
                      const details = Array.isArray(project.details) ? project.details : [];
                      const requiredN = projectRequiredNText(project);
                      const overallBreakdown = metrics.overall_breakdown || emptyBreakdown;
                      const twentyFourHourStats = overallBreakdown["24h"] || emptyBreakdown["24h"];
                      const hasUploadedFinalIds =
                        activeTab === "closed" && project.has_final_ids === true;

                      return (
                        <React.Fragment key={project.id}>
                          <TableRow
                            hover
                            sx={{
                              transition: "background-color 150ms ease",
                              ...(hasUploadedFinalIds
                                ? {
                                    "& > td": { backgroundColor: "#dcfce7" },
                                    "& > td:first-of-type": {
                                      borderLeft: "4px solid #16a34a",
                                    },
                                    "&:hover > td": { backgroundColor: "#bbf7d0" },
                                  }
                                : { "&:hover": { backgroundColor: "#f8fafc" } }),
                            }}
                          >
                            <TableCell sx={tableBodyCellSx}>
                              {questCodeButton(project)}
                              {canViewClient && (
                                <SoftTypography
                                  variant="caption"
                                  color="text"
                                  display="block"
                                  sx={{ fontSize: "10px", fontWeight: 400, lineHeight: 1.1 }}
                              >
                                <Tooltip title={textOrDash(clientText(project))}>
                                  <SoftBox component="span">{shortClientText(project, 5)}</SoftBox>
                                </Tooltip>
                              </SoftTypography>
                            )}
                            </TableCell>
                            {canViewProjectTitle && <TableCell
                              sx={{
                                ...compactMetricCellSx,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              <Tooltip title={textOrDash(project.project_title)}>
                                <SoftBox component="span">{textOrDash(project.project_title)}</SoftBox>
                              </Tooltip>
                            </TableCell>}
                            {canViewProjectManager && <TableCell
                              sx={{ ...tableBodyCellSx, borderLeft: "1px solid #edf2f7" }}
                            >
                              {textOrDash(project.project_manager?.name || project.project_manager?.email)}
                            </TableCell>}
                            {canViewRequiredN && (
                              <TableCell sx={compactMetricCellSx}>
                                {requiredN}
                              </TableCell>
                            )}
                            {canViewIr && <TableCell sx={compactMetricCellSx}>
                              <IrMetric
                                average={metrics.average_ir}
                                configured={projectConfiguredIr(project)}
                              />
                            </TableCell>}
                            {canViewLoi && <TableCell sx={compactMetricCellSx}>
                              <LoiMetric
                                average={project.project_metrics?.average_loi}
                                configured={projectConfiguredLoi(project)}
                              />
                            </TableCell>}
                            {canViewStudyType && activeTab !== "live" && <TableCell sx={compactMetricCellSx}>{textOrDash(studyTypeText(project))}</TableCell>}
                            {(canViewProjectType || (activeTab === "live" && canViewStudyType)) && (
                              <TableCell sx={compactMetricCellSx}>
                                {canViewProjectType && textOrDash(projectTypeText(project))}
                                {activeTab === "live" && canViewStudyType && (
                                  <SoftBox
                                    component="div"
                                    sx={{
                                      fontSize: "0.65rem !important",
                                      lineHeight: 1.05,
                                      fontWeight: 400,
                                      color: "text.secondary",
                                      mt: canViewProjectType ? 0.15 : 0,
                                    }}
                                  >
                                    {textOrDash(studyTypeText(project))}
                                  </SoftBox>
                                )}
                              </TableCell>
                            )}
                            {canViewCpi && <TableCell sx={compactMetricCellSx}>{calculatedCpiText(project)}</TableCell>}
                            {canViewCompleteDeviceBrowser && (
                              <TableCell sx={{ ...compactMetricCellSx, minWidth: 0, px: 0.05 }}>
                                <CompleteDeviceMetric
                                  metric={metrics.complete_device_metrics}
                                  total={stats.complete}
                                />
                              </TableCell>
                            )}
                            {canViewStats && (
                              <>
                                <TableCell sx={statsMetricCellSx}>
                                  <StatsHoverTooltip
                                    label={String(project.quest_code || "Project Code")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span" sx={{ display: "inline-flex", fontSize: "inherit", fontWeight: 700 }}>
                                      <SoftBox component="span" sx={{ color: Number(stats.complete || 0) > 0 ? "#16a34a" : "#dc2626" }}>
                                        {Number(stats.complete || 0)}
                                      </SoftBox>
                                      <SoftBox component="span" sx={{ color: "#0ea5e9" }}>/{quotaText(project.overall_quota)}</SoftBox>
                                    </SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                {activeTab === "live" && (
                                  <TableCell sx={{ ...statsMetricCellSx, backgroundColor: "#faf5ff" }}>
                                    <SoftBox
                                      component="span"
                                      sx={{ display: "inline-flex", alignItems: "center", fontSize: "inherit", fontWeight: 700 }}
                                    >
                                      <SoftBox component="span" sx={{ color: "#16a34a" }}>
                                        {Number(twentyFourHourStats.complete || 0)}
                                      </SoftBox>
                                      <SoftBox component="span" sx={{ color: "#64748b" }}>/</SoftBox>
                                      <SoftBox component="span" sx={{ color: "#0ea5e9" }}>
                                        {Number(twentyFourHourStats.clicks || 0)}
                                      </SoftBox>
                                    </SoftBox>
                                  </TableCell>
                                )}
                                <TableCell sx={{ ...statsMetricCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(project.quest_code || "Project Code")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.terminate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...statsMetricCellSx, color: "#d97706", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(project.quest_code || "Project Code")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.quota_full || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...statsMetricCellSx, color: "#dc2626", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(project.quest_code || "Project Code")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.security_terminate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                                <TableCell sx={{ ...statsMetricCellSx, color: "#0f766e", backgroundColor: "#ecfeff", fontWeight: 700 }}>
                                  <StatsHoverTooltip
                                    label={String(project.quest_code || "Project Code")}
                                    stats={stats}
                                  >
                                    <SoftBox component="span">{Number(stats.initiate || 0)}</SoftBox>
                                  </StatsHoverTooltip>
                                </TableCell>
                              </>
                            )}
                            {canViewInternalUserMetrics && metricVendorColumns.map((vendor, index) => {
                              const source = metricSourceForVendor(project, vendor);

                              return (
                                <TableCell
                                  key={`${project.id}-${vendor.id}-${index}`}
                                  sx={{
                                    ...compactMetricCellSx,
                                    backgroundColor: index % 2 === 0 ? "#f1f5f9" : "#fff1f2",
                                  }}
                                >
                                  <Tooltip
                                    placement="bottom"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          p: 0,
                                          maxWidth: "none",
                                          backgroundColor: "transparent",
                                          boxShadow: "none",
                                        },
                                      },
                                    }}
                                    title={
                                      <MetricHoverCard
                                        label={vendor.name}
                                        breakdown={source.breakdown || emptyBreakdown}
                                      />
                                    }
                                  >
                                    <SoftBox
                                      component="span"
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        width: "100%",
                                        minHeight: 17,
                                        cursor: "help",
                                      }}
                                    >
                                      {Number(source.count || 0)}
                                    </SoftBox>
                                  </Tooltip>
                                </TableCell>
                              );
                            })}
                            {canViewInternalUserMetrics && (
                            <TableCell
                              sx={{
                                ...compactMetricCellSx,
                                color: "#0284c7",
                                backgroundColor: "#e0f2fe",
                              }}
                            >
                              <Tooltip
                                placement="bottom"
                                componentsProps={{
                                  tooltip: {
                                    sx: {
                                      p: 0,
                                      maxWidth: "none",
                                      backgroundColor: "transparent",
                                      boxShadow: "none",
                                    },
                                  },
                                }}
                                title={
                                  <MetricHoverCard
                                    label="External Vendors"
                                    breakdown={vendors.breakdown || emptyBreakdown}
                                  />
                                }
                              >
                                <SoftBox
                                  component="span"
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: "100%",
                                    minHeight: 17,
                                    cursor: "help",
                                  }}
                                >
                                  {Number(vendors.count || 0)}
                                </SoftBox>
                              </Tooltip>
                            </TableCell>
                            )}
                            {canViewRtr && <TableCell sx={{ ...compactMetricCellSx, backgroundColor: "#fee2e2" }}>
                              {percentageText(metrics.rtr)}
                            </TableCell>}
                            {showEpc && <TableCell sx={{ ...compactMetricCellSx, backgroundColor: "#e0f2fe" }}>
                              {epcText(metrics.revenue, overallBreakdown, stats)}
                            </TableCell>}
                            {canViewRevenue && <TableCell sx={{ ...compactMetricCellSx, color: "#15803d" }}>
                              {currencyText(metrics.revenue)}
                            </TableCell>}
                            {canViewStatus && <TableCell sx={compactMetricCellSx}>
                              {statusBadge(project)}
                            </TableCell>}
                            {showProjectRowActions && <TableCell sx={compactMetricCellSx}>
                              <SoftBox display="flex" justifyContent="center" alignItems="center" gap={0.15}>
                                {canViewComments && <Tooltip title={project.comments || "No comments"}>
                                  <IconButton
                                    size="small"
                                    color="warning"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openProjectCommunicationDialog("comment", project)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>comment</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {projectPermissions.canAction("mail") && <Tooltip title="Mail">
                                  <IconButton
                                    size="small"
                                    color="info"
                                    sx={{ p: 0.2 }}
                                    onClick={() => openProjectCommunicationDialog("mail", project)}
                                  >
                                    <Icon sx={{ fontSize: "0.85rem !important" }}>mail</Icon>
                                  </IconButton>
                                </Tooltip>}
                                {canUseProjectLiveChat && <Tooltip title="Project live chat">
                                  <IconButton
                                    size="small"
                                    color="success"
                                    sx={{ p: 0.2 }}
                                    onClick={() =>
                                      openProjectLiveChat({
                                        id: project.id,
                                        quest_code: project.quest_code || project.project_code || project.id,
                                        project_title:
                                          project.project_title ||
                                          project.project_name ||
                                          project.quest_code ||
                                          project.project_code ||
                                          "",
                                      })
                                    }
                                  >
                                    <ProjectLiveChatBadge projectId={project.id}>
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>forum</Icon>
                                    </ProjectLiveChatBadge>
                                  </IconButton>
                                </Tooltip>}
                                {canEditProjectDetails(project) && (
                                  <Tooltip title="Edit project details">
                                    <IconButton
                                      size="small"
                                      onClick={() => openProjectQuotaDialog(project)}
                                      sx={{ p: 0.2, color: "#344767" }}
                                    >
                                      <Icon sx={{ fontSize: "0.85rem !important" }}>edit</Icon>
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {canExpandDetails && <Tooltip title={isExpanded ? "Collapse details" : "Expand details"}>
                                  <IconButton
                                    size="small"
                                    color="info"
                                    onClick={() => toggleProjectDetails(project.id)}
                                    sx={{ p: 0.2 }}
                                  >
                                    <Icon sx={{ fontSize: "0.9rem !important" }}>
                                      {isExpanded ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                                    </Icon>
                                  </IconButton>
                                </Tooltip>}
                              </SoftBox>
                            </TableCell>}
                            {showProjectStatusActionColumn && <TableCell sx={{ ...compactMetricCellSx, px: 0 }}>
                              {projectActionButton(project)}
                            </TableCell>}
                          </TableRow>
                          {canExpandDetails && isExpanded && (
                            <TableRow>
                              <TableCell colSpan={liveColumnCount} sx={{ p: 0, borderBottom: "1px solid #cbd5e1" }}>
                                <SoftBox sx={{ p: 2, backgroundColor: "#e5e7eb" }}>
                                  <SoftBox
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: { xs: "1fr", md: "2fr 1fr" },
                                      gap: 1,
                                      mb: 1.5,
                                    }}
                                  >
                                    {canViewProjectTitle && <SoftBox>
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="regular"
                                        color="dark"
                                        sx={{ fontSize: "12px" }}
                                      >
                                        Project Name
                                      </SoftTypography>
                                      <SoftTypography
                                        variant="body2"
                                        color="dark"
                                        sx={{ fontSize: "10px", fontWeight: 400 }}
                                      >
                                        {textOrDash(project.project_title)}
                                      </SoftTypography>
                                    </SoftBox>}
                                    {canViewRequiredN && <SoftBox>
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="regular"
                                        color="dark"
                                        sx={{ fontSize: "12px" }}
                                      >
                                        Required N
                                      </SoftTypography>
                                      <SoftTypography
                                        variant="body2"
                                        color="dark"
                                        sx={{ fontSize: "10px", fontWeight: 400 }}
                                      >
                                        {requiredN || "-"}
                                      </SoftTypography>
                                    </SoftBox>}
                                  </SoftBox>

                                  <SoftBox
                                    sx={{
                                      display: "grid",
                                      gridTemplateColumns: { xs: "1fr", lg: "minmax(430px, 0.8fr) 1.2fr" },
                                      gap: 2,
                                    }}
                                  >
                                    {canViewStats && <SoftBox
                                      sx={{
                                        display: "grid",
                                        gridTemplateColumns: "90px repeat(4, minmax(60px, 1fr))",
                                        alignSelf: "start",
                                        backgroundColor: "#f8fafc",
                                        borderTop: "1px solid #94a3b8",
                                        borderLeft: "1px solid #94a3b8",
                                      }}
                                    >
                                      {["", ...metricPeriods.map((period) => period.label)].map((heading, index) => (
                                        <SoftBox
                                          key={`expanded-heading-${heading || index}`}
                                          sx={{
                                            p: 0.65,
                                            textAlign: index === 0 ? "left" : "center",
                                            borderRight: "1px solid #94a3b8",
                                            borderBottom: "1px solid #94a3b8",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                          }}
                                        >
                                          {heading}
                                        </SoftBox>
                                      ))}
                                        {[
                                          { label: "Complete", key: "complete" },
                                          { label: "Clicks", key: "clicks" },
                                          ...(canViewRtr ? [{ label: "V.RTR", key: "v_rtr" }] : []),
                                        ].flatMap((row) => [
                                        <SoftBox
                                          key={`expanded-${row.key}-label`}
                                          sx={{
                                            p: 0.65,
                                            borderRight: "1px solid #94a3b8",
                                            borderBottom: "1px solid #94a3b8",
                                            fontSize: "12px",
                                            fontWeight: 400,
                                          }}
                                        >
                                          {row.label}
                                        </SoftBox>,
                                        ...metricPeriods.map((period) => (
                                          <SoftBox
                                            key={`expanded-${row.key}-${period.key}`}
                                            sx={{
                                              p: 0.65,
                                              textAlign: "center",
                                              borderRight: "1px solid #94a3b8",
                                              borderBottom: "1px solid #94a3b8",
                                              fontSize: "10px",
                                              fontWeight: 400,
                                            }}
                                          >
                                            {overallBreakdown?.[period.key]?.[row.key] ?? "-"}
                                          </SoftBox>
                                        )),
                                      ])}
                                    </SoftBox>}

                                    {(canViewCountry || canViewRequiredN || canViewIr || canViewLoi || canViewCpi) && <SoftBox>
                                      <SoftTypography
                                        variant="caption"
                                        fontWeight="regular"
                                        color="dark"
                                        sx={{ fontSize: "12px" }}
                                      >
                                        Country Details
                                      </SoftTypography>
                                      <SoftBox
                                        sx={{
                                          display: "grid",
                                          gridTemplateColumns: {
                                            xs: "1fr",
                                            md: "repeat(2, minmax(0, 1fr))",
                                          },
                                          gap: 1,
                                          mt: 0.75,
                                        }}
                                      >
                                        {details.length === 0 ? (
                                          <SoftTypography variant="caption" color="text">
                                            No country details available.
                                          </SoftTypography>
                                        ) : (
                                          details.map((detail) => (
                                            <SoftBox
                                              key={detail.id || `${project.id}-${detail.country_id}`}
                                              sx={{
                                                p: 1,
                                                backgroundColor: "#f8fafc",
                                                border: "1px solid #cbd5e1",
                                                borderRadius: "6px",
                                              }}
                                            >
                                              <SoftTypography
                                                variant="caption"
                                                fontWeight="regular"
                                                color="dark"
                                                sx={{ fontSize: "12px" }}
                                              >
                                                {canViewCountry ? textOrDash(detail.country_name || detail.country?.name) : "Country"}
                                              </SoftTypography>
                                              <SoftTypography
                                                variant="caption"
                                                color="text"
                                                display="block"
                                                sx={{ fontSize: "10px", fontWeight: 400 }}
                                              >
                                                {[
                                                  canViewRequiredN ? `N: ${textOrDash(detail.required_n)}` : null,
                                                  canViewIr ? `IR(%): ${formatIrDisplay(detail.ir)}` : null,
                                                  canViewLoi ? `LOI(mins): ${textOrDash(detail.loi)}` : null,
                                                  canViewCpi ? `CPI: ${detail.won_cpi ? `$${detail.won_cpi}` : "-"}` : null,
                                                ].filter(Boolean).join(" | ") || "-"}
                                              </SoftTypography>
                                            </SoftBox>
                                          ))
                                        )}
                                      </SoftBox>
                                    </SoftBox>}
                                  </SoftBox>
                                </SoftBox>
                              </TableCell>
                            </TableRow>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

        </Card>

        <Dialog
          open={Boolean(invoiceAtZeroProject)}
          onClose={closeInvoiceAtZeroConfirmation}
          maxWidth="xs"
          fullWidth
          PaperProps={{ sx: { borderRadius: "12px" } }}
        >
          <DialogTitle sx={{ fontSize: "18px", fontWeight: 600, pb: 1 }}>
            Confirm Invoice at Zero
          </DialogTitle>
          <DialogContent>
            <SoftTypography variant="body2" color="text">
              Are you sure you want to mark status Invoice at Zero for this project code:{" "}
              <SoftTypography component="span" variant="body2" fontWeight="bold" color="dark">
                {invoiceAtZeroProject?.quest_code || invoiceAtZeroProject?.project_code || "-"}
              </SoftTypography>
              ?
            </SoftTypography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
            <SoftButton
              variant="outlined"
              color="dark"
              size="small"
              disabled={statusLoading}
              onClick={closeInvoiceAtZeroConfirmation}
            >
              Cancel
            </SoftButton>
            <SoftButton
              variant="gradient"
              color="dark"
              size="small"
              disabled={statusLoading}
              onClick={confirmInvoiceAtZero}
            >
              {statusLoading ? <CircularProgress size={16} color="inherit" /> : "Confirm"}
            </SoftButton>
          </DialogActions>
        </Dialog>

        <Dialog
          open={quotaDialogOpen}
          onClose={closeProjectQuotaDialog}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: "8px" } }}
        >
          <DialogTitle sx={{ fontSize: "18px", py: 1.25 }}>
            {pendingLaunchStatus
              ? `Review Project Details - ${
                  projectStatusActions.find((action) => action.value === pendingLaunchStatus)?.label ||
                  "Launch"
                }`
              : "Edit Project Details"}
          </DialogTitle>
          <DialogContent sx={{ pt: 0.5, pb: 1 }}>
            <SoftBox
              display="grid"
              gridTemplateColumns={{ xs: "1fr", md: "repeat(3, minmax(0, 1fr))" }}
              gap={1}
            >
              {canViewProjectCode && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Code
                </SoftTypography>
                <SoftTypography variant="body2" color="text" sx={{ fontSize: "12px" }}>
                  {textOrDash(quotaDialogProject?.quest_code)}
                </SoftTypography>
              </SoftBox>}
              {canViewClient && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Client
                </SoftTypography>
                <SoftSelect
                  compact
                  size="small"
                  placeholder="Search client"
                  value={quotaForm.client}
                  options={mergeSelectOptions(clients, quotaForm.client)}
                  isLoading={clientsLoading}
                  onInputChange={(value) => setClientSearch(value)}
                  onMenuOpen={() => fetchClients(clientSearch)}
                  onChange={(value) => updateQuotaForm("client", value)}
                  isSearchable
                  isClearable
                />
              </SoftBox>}
              {canViewProjectManagerTeam && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Management Team
                </SoftTypography>
                <SoftSelect
                  compact
                  size="small"
                  placeholder="Select PM Team"
                  value={quotaForm.project_manager_role}
                  options={mergeSelectOptions(projectManagerRoles, quotaForm.project_manager_role)}
                  isLoading={projectManagersLoading}
                  onMenuOpen={fetchProjectManagers}
                  onChange={(value) => updateQuotaForm("project_manager_role", value)}
                  isSearchable
                  isClearable
                />
              </SoftBox>}
              {canViewProjectManager && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Manager *
                </SoftTypography>
                <SoftSelect
                  compact
                  size="small"
                  placeholder={
                    quotaProjectManagerRoleSelected ? "Select Project Manager" : "Select PM Team first"
                  }
                  value={quotaForm.project_manager}
                  options={mergeSelectOptions(quotaProjectManagerUsers, quotaForm.project_manager)}
                  isLoading={projectManagersLoading}
                  isDisabled={!quotaProjectManagerRoleSelected}
                  onMenuOpen={fetchProjectManagers}
                  onChange={(value) => updateQuotaForm("project_manager", value)}
                  isSearchable
                  isClearable
                />
              </SoftBox>}
              {canViewStudyType && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Study Type
                </SoftTypography>
                <SoftSelect
                  compact
                  size="small"
                  placeholder="Select Study Type"
                  value={quotaForm.project_type}
                  options={mergeSelectOptions(projectTypes, quotaForm.project_type)}
                  isLoading={projectTypesLoading}
                  onMenuOpen={fetchProjectTypes}
                  onChange={(value) => updateQuotaForm("project_type", value)}
                  isSearchable
                  isClearable
                />
              </SoftBox>}
              {canViewProjectType && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Type
                </SoftTypography>
                <SoftSelect
                  compact
                  size="small"
                  placeholder="Select Project Type"
                  value={quotaForm.project_type_option}
                  options={mergeSelectOptions(bidProjectTypeOptions, quotaForm.project_type_option)}
                  isLoading={bidProjectTypeOptionsLoading}
                  onMenuOpen={fetchBidProjectTypeOptions}
                  onChange={(value) => updateQuotaForm("project_type_option", value)}
                  isSearchable
                  isClearable
                />
              </SoftBox>}
              {canViewProjectTitle && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Title
                </SoftTypography>
                <SoftInput
                  size="small"
                  value={quotaForm.project_title}
                  placeholder="Project Title"
                  onChange={(event) => updateQuotaForm("project_title", event.target.value)}
                />
              </SoftBox>}
              {canViewOverallQuota && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Overall Completes Quota (Cap)
                </SoftTypography>
                <SoftInput
                  size="small"
                  value={quotaForm.overall_quota}
                  placeholder="Overall Completes Quota (Cap)"
                  onChange={(event) => updateQuotaForm("overall_quota", event.target.value)}
                />
              </SoftBox>}
              {canViewOverallQuota && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Overall Clicks Quota (Cap)
                </SoftTypography>
                <SoftInput
                  size="small"
                  value={quotaForm.overall_clicks_quota}
                  placeholder="Overall Clicks Quota (Cap)"
                  onChange={(event) =>
                    updateQuotaForm("overall_clicks_quota", event.target.value)
                  }
                />
              </SoftBox>}
              {activeTab === "live" &&
                canSubmitProjectDetails &&
                canManageProjectLinkHitGap && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Link Hit Gap (Seconds)
                </SoftTypography>
                <SoftInput
                  size="small"
                  type="number"
                  value={quotaForm.link_hit_gap_seconds}
                  placeholder="NULL"
                  inputProps={{ min: 1, max: 86400, step: 1 }}
                  onChange={(event) =>
                    updateQuotaForm("link_hit_gap_seconds", event.target.value)
                  }
                />
              </SoftBox>}
              {canViewProjectLaunchDate && <SoftBox>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Project Launch Date
                </SoftTypography>
                <SoftInput
                  size="small"
                  type="datetime-local"
                  value={quotaForm.project_launch_date}
                  onChange={(event) => updateQuotaForm("project_launch_date", event.target.value)}
                />
              </SoftBox>}
              {activeTab === "live" && <SoftBox gridColumn={{ xs: "1", md: "1 / -1" }}>
                <FormControlLabel
                  sx={{ m: 0, alignItems: "center", gap: 0.5 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={Boolean(quotaForm.is_verisoul_security)}
                      onChange={(event) =>
                        updateQuotaForm("is_verisoul_security", event.target.checked)
                      }
                      sx={{
                        p: 0,
                        color: "#d2d6da",
                        "&.Mui-checked": { color: "#17c1e8" },
                      }}
                    />
                  }
                  label={
                    <SoftTypography variant="caption" fontWeight="bold" sx={{ color: "#17c1e8" }}>
                      Enable Verisoul Security
                    </SoftTypography>
                  }
                />
              </SoftBox>}
              {canViewComments && <SoftBox gridColumn={{ xs: "1", sm: "1 / -1" }}>
                <SoftTypography variant="caption" fontWeight="bold" color="dark">
                  Comments
                </SoftTypography>
                <SoftInput
                  size="small"
                  multiline
                  rows={2}
                  value={quotaForm.comments}
                  placeholder="Comments"
                  onChange={(event) => updateQuotaForm("comments", event.target.value)}
                />
              </SoftBox>}
            </SoftBox>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 1.5, pt: 0 }}>
            <SoftButton variant="outlined" color="dark" size="small" onClick={closeProjectQuotaDialog}>
              Close
            </SoftButton>
            {canSubmitProjectDetails && (
              <SoftButton
                variant="gradient"
                color="info"
                size="small"
                disabled={quotaSubmitLoading}
                onClick={submitProjectQuota}
              >
                {quotaSubmitLoading
                  ? "Saving..."
                  : pendingLaunchStatus
                  ? `Save & ${
                      projectStatusActions.find(
                        (action) => action.value === pendingLaunchStatus
                      )?.label || "Launch"
                    }`
                  : "Save"}
              </SoftButton>
            )}
          </DialogActions>
        </Dialog>

        <ProjectCommunicationComposerDialog
          open={Boolean(communicationDialog)}
          type={communicationDialog?.type}
          scope={communicationDialog?.scope}
          onClose={() => setCommunicationDialog(null)}
          onSent={fetchProjects}
          onNotify={showMessage}
        />

        <Menu
          anchorEl={actionsAnchor}
          open={Boolean(actionsAnchor)}
          onClose={closeActionsMenu}
          PaperProps={{
            sx: {
              minWidth: 190,
              borderRadius: "8px",
              boxShadow: "0 12px 32px rgba(20, 20, 43, 0.18)",
              py: 1,
            },
          }}
        >
          {allowedProjectStatusActions.map((action) => {
            const isCurrent = String(actionsProject?.status || "won") === action.value;

            return (
              <MenuItem
                key={action.value}
                disabled={statusLoading || isCurrent}
                onClick={() => handleProjectStatusSelection(actionsProject, action.value)}
                sx={{ color: action.color, fontWeight: 600, fontSize: "0.875rem" }}
              >
                {action.label}
                {statusLoading && !isCurrent ? (
                  <CircularProgress size={16} color="inherit" sx={{ ml: 1 }} />
                ) : null}
              </MenuItem>
            );
          })}
        </Menu>
      </SoftBox>

      <Footer />
    </DashboardLayout>
  );
}

export default ProjectsList;
